import { formatLocalDate, formatLocalDateTime, formatLocalDateValue } from './localDateTime';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { redactDailyMicroReport } from './dailyMicroRedaction';
import { EventSecurityPlanResult, EpThreatAnalysisResult, EpOpsAnalysisResult, HSSEIncidentReport, HSSEComplianceBriefing, AuditRemediationResult, GeopoliticalGovernmentAnalysisResult, SrmProject } from '@types';
import { isUnitedStatesReport, US_ENTRY_SUMMARY } from '../common/usEntryGuidance';
import { cleanReportPromptText } from '../../functions/src/common/reportPromptText';
import { getLeadershipStatus } from '../../functions/src/common/leadershipStatus';
import { filterDailyMicroEvents } from './dailyMicroEvents';
import { quantitativeIndicatorGroups } from './quantitativeIndicators';
import { getCertificateSignatories } from './learnerCertificate';
import { srmModuleReportData, srmModuleReportText } from '../../functions/src/common/srmModuleReports';
import { cleanSrmSummary } from '../common/srmSummary';
import { enrichEconomicIntelligence } from '../../functions/src/common/economicIntelligence';
import { mergeFullReportData } from './reportDataMerge';

/**
 * SLATE ON WHITE professional theme constants
 */
const COLORS = {
    PRIMARY: [30, 41, 59] as [number, number, number], // Slate-800
    SECONDARY: [71, 85, 105] as [number, number, number], // Slate-600
    ACCENT: [37, 99, 235] as [number, number, number], // Blue-600
    TEXT: [15, 23, 42] as [number, number, number], // Slate-950
    LIGHT_TEXT: [100, 116, 139] as [number, number, number], // Slate-400
    BORDER: [226, 232, 240] as [number, number, number], // Slate-200
    WHITE: [255, 255, 255] as [number, number, number],
    BADGE_BG: [239, 68, 68] as [number, number, number] // Red-500
};

const getDTG = () => formatLocalDateTime(new Date());

const formatPdfDateTime = (value: unknown) => formatLocalDateTime(value, 'N/A');

/**
 * Sanitizes a string for use as a filename across Windows/Linux/macOS
 */
const sanitizeFilename = (name: string): string => {
    return name
        .replace(/[:\\/?*<>|"]/g, '_') // Replace illegal characters with underscore
        .replace(/\s+/g, '_')          // Replace spaces with underscores
        .replace(/_{2,}/g, '_')       // Collapse multiple underscores
        .trim();
};

/**
 * Shared branding helper for multi-page tables
 */
const drawGlobalBranding = (doc: jsPDF, dtg: string, pageWidth: number, pageHeight: number, margin: number) => {
    doc.setFillColor(...COLORS.PRIMARY);
    doc.rect(0, 0, pageWidth, 2.5, 'F');
    doc.setFont('helvetica', 'bold').setFontSize(8).setTextColor(...COLORS.LIGHT_TEXT);
    doc.text("INTELOPTICS INSIGHTS // STRATEGIC RISK MANAGEMENT", margin, 10);
    doc.setFontSize(8).setTextColor(...COLORS.BADGE_BG);
    doc.text("CONFIDENTIAL // INTERNAL USE ONLY", pageWidth - margin, 10, { align: 'right' });
    doc.setFont('helvetica', 'normal').setFontSize(7).setTextColor(...COLORS.LIGHT_TEXT);
    doc.text(`DTG: ${dtg} | GENERATED BY INTELOPTICS CORE`, margin, pageHeight - 10);
    doc.text(`PAGE ${doc.getNumberOfPages()}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
};

/**
 * Robust download trigger to bypass CSP/Blob issues
 */
const forceDownload = (doc: jsPDF, fileName: string) => {
    try {
        const blob = doc.output('blob');
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        
        // Cleanup with slight delay to ensure browser handles the request
        setTimeout(() => {
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }, 100);
    } catch (error) {
        console.error("PDF Download failed, falling back to basic save:", error);
        doc.save(fileName);
    }
};

/**
 * HIGH-FIDELITY PDF for GeopoliticalAnalysisResult / ComprehensiveGeopoliticalAnalysis.
 * Mirrors all major sections rendered in GeopoliticalAnalysisDisplay.tsx.
 */
export const generateGeopoliticalAnalysisPdf = async (result: any) => {
    result = cleanReportPromptText(result);
    const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
    const dtg = getDTG();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;

    // ── data normalisation (handles fullReportData wrapper) ──
    const data = result.fullReportData || result;
    const country = data.country || result.country || result.countryName || 'Unknown Territory';
    const riskScore = Number(data.riskScore || result.riskScore || 0);
    const overallRisk = (data.overallRiskLevel || result.overallRiskLevel || 'UNKNOWN').toUpperCase();

    const RISK_COLOR = (level: string): [number, number, number] => {
        if (level === 'CRITICAL') return [185, 28, 28];
        if (level === 'HIGH')     return [234, 88, 12];
        if (level === 'MEDIUM')   return [202, 138, 4];
        return [22, 163, 74];
    };

    const applyBranding = () => {
        doc.setFillColor(...COLORS.PRIMARY).rect(0, 0, pageWidth, 2.5, 'F');
        doc.setFont('helvetica', 'bold').setFontSize(7).setTextColor(...COLORS.LIGHT_TEXT);
        doc.text('INTELOPTICS INSIGHTS // GEOPOLITICAL INTELLIGENCE DIVISION', margin, 8);
        doc.setFontSize(7).setTextColor(...COLORS.BADGE_BG);
        doc.text('CONFIDENTIAL // INTERNAL USE ONLY', pageWidth - margin, 8, { align: 'right' });
        doc.setFont('helvetica', 'normal').setFontSize(6.5).setTextColor(...COLORS.LIGHT_TEXT);
        doc.text(`DTG: ${dtg}  |  INTELOPTICS CORE v2.0`, margin, pageHeight - 7);
        doc.text(`PAGE ${doc.getNumberOfPages()}`, pageWidth - margin, pageHeight - 7, { align: 'right' });
    };

    const addSection = (title: string, y: number): number => {
        if (y > pageHeight - 40) { doc.addPage(); applyBranding(); y = 20; }
        doc.setFillColor(...COLORS.ACCENT).rect(margin, y, 4, 7, 'F');
        doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(...COLORS.PRIMARY);
        doc.text(title, margin + 7, y + 5.5);
        return y + 13;
    };

    const addText = (text: string, y: number, size = 9): number => {
        const lines = doc.splitTextToSize(String(text || ''), contentWidth);
        doc.setFont('helvetica', 'normal').setFontSize(size).setTextColor(...COLORS.TEXT);
        lines.forEach((l: string) => {
            if (y > pageHeight - 18) { doc.addPage(); applyBranding(); y = 20; }
            doc.text(l, margin, y);
            y += size * 0.6;
        });
        return y + 4;
    };

    // ── PAGE 1 — COVER ──────────────────────────────────────────────────────
    applyBranding();
    let y = 18;

    // Heading block
    doc.setFont('helvetica', 'bold').setFontSize(22).setTextColor(...COLORS.PRIMARY);
    const titleLines = doc.splitTextToSize(`GEOPOLITICAL INTELLIGENCE BRIEF: ${country.toUpperCase()}`, contentWidth - 45);
    doc.text(titleLines, margin, y);
    y += titleLines.length * 10;

    // Risk badge (top-right)
    const badgeColor = RISK_COLOR(overallRisk);
    doc.setFillColor(...badgeColor).roundedRect(pageWidth - margin - 40, 16, 40, 16, 2, 2, 'F');
    doc.setFont('helvetica', 'bold').setFontSize(8).setTextColor(255, 255, 255);
    doc.text(overallRisk, pageWidth - margin - 20, 22, { align: 'center' });
    doc.setFontSize(7);
    doc.text(`RISK: ${riskScore > 0 ? riskScore.toFixed(1) : 'N/A'}/10`, pageWidth - margin - 20, 28, { align: 'center' });

    // Sub-meta row
    doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(...COLORS.SECONDARY);
    const analysisDate = formatLocalDate(data.analysis_date || data.date || result.date || new Date());
    doc.text(`Analysis Date: ${analysisDate}  |  Classification: CONFIDENTIAL  |  Origin: INTELOPTICS ECC`, margin, y);
    y += 5;
    doc.setDrawColor(...COLORS.BORDER).line(margin, y, pageWidth - margin, y);
    y += 8;

    // ── EXECUTIVE SUMMARY ──────────────────────────────────────────────────
    y = addSection('I. EXECUTIVE SUMMARY', y);
    const summary = data.summary || data.executiveSummary || data.riskSummary || result.riskSummary || '';
    y = addText(summary, y, 9);

    if (data.governmentStability) {
        doc.setFillColor(248, 250, 252).rect(margin, y, contentWidth, 14, 'F');
        doc.setDrawColor(...COLORS.BORDER).rect(margin, y, contentWidth, 14);
        doc.setFont('helvetica', 'bold').setFontSize(8).setTextColor(...COLORS.PRIMARY);
        doc.text('GOVERNMENT STABILITY ASSESSMENT:', margin + 3, y + 5.5);
        doc.setFont('helvetica', 'normal').setFontSize(8).setTextColor(...COLORS.TEXT);
        const stabLines = doc.splitTextToSize(String(data.governmentStability), contentWidth - 60);
        doc.text(stabLines[0] || '', margin + 3, y + 11);
        y += 20;
    }

    // Trending status pill
    const trendingRaw = data.trendingStatus || data.trending || result.trending;
    const trending = typeof trendingRaw === 'string' ? trendingRaw : (trendingRaw as any)?.status;
    if (trending) {
        const isStable = trending.toLowerCase().includes('stable');
        doc.setFillColor(...(isStable ? [220, 252, 231] as [number,number,number] : [254, 243, 199] as [number,number,number]));
        doc.rect(margin, y, 55, 8, 'F');
        doc.setFont('helvetica', 'bold').setFontSize(7.5).setTextColor(...(isStable ? [21, 128, 61] as [number,number,number] : [146, 64, 14] as [number,number,number]));
        doc.text(`TRENDING: ${trending.toUpperCase()}`, margin + 3, y + 5.5);
        y += 14;
    }

    // ── QUANTITATIVE INDICATORS ────────────────────────────────────────────
    const qIndicators = data.quantitativeIndicators || result.quantitativeIndicators;
    if (qIndicators?.length > 0) {
        if (y > pageHeight - 40) { doc.addPage(); applyBranding(); y = 20; }
        y = addSection('II. QUANTITATIVE INDICATORS', y);
        const rows = qIndicators.map((i: any) => [i.label, String(i.value) + (i.unit ? ` ${i.unit}` : ''), formatLocalDate(i.date, i.date || 'Current'), (i.trend || '—').toUpperCase()]);
        autoTable(doc, {
            startY: y, head: [['INDICATOR', 'VALUE', 'DATE', 'TREND']],
            body: rows, theme: 'grid',
            headStyles: { fillColor: COLORS.PRIMARY, fontSize: 8 },
            styles: { fontSize: 8, cellPadding: 2.5, textColor: COLORS.TEXT },
            columnStyles: { 1: { fontStyle: 'bold' } },
            margin: { left: margin, right: margin }, didDrawPage: () => applyBranding()
        });
        y = (doc as any).lastAutoTable.finalY + 10;
    }

    // ── RISK FACTORS MATRIX ────────────────────────────────────────────────
    const riskFactors = data.riskFactors || result.riskFactors;
    if (riskFactors?.length > 0) {
        if (y > pageHeight - 40) { doc.addPage(); applyBranding(); y = 20; }
        y = addSection('III. INTEGRATED GEOPOLITICAL RISK MATRIX', y);
        const rfRows = riskFactors.map((r: any) => {
            const factor = typeof r === 'string' ? r : (r.factor || r.name || 'Risk Factor');
            const score = typeof r === 'object' ? String(r.score ?? '—') : '—';
            const rating = typeof r === 'object' ? ((r.score >= 80 ? 'CRITICAL' : r.score >= 50 ? 'HIGH' : 'LOW')) : '—';
            return [factor, score, rating];
        });
        autoTable(doc, {
            startY: y, head: [['RISK FACTOR', 'SCORE', 'RATING']],
            body: rfRows, theme: 'striped',
            headStyles: { fillColor: COLORS.ACCENT, fontSize: 8 },
            styles: { fontSize: 8, cellPadding: 2.5, textColor: COLORS.TEXT },
            columnStyles: { 1: { cellWidth: 18, halign: 'center' }, 2: { cellWidth: 22, halign: 'center', fontStyle: 'bold' } },
            margin: { left: margin, right: margin }, didDrawPage: () => applyBranding()
        });
        y = (doc as any).lastAutoTable.finalY + 10;
    }

    // ── PMESII-PT ─────────────────────────────────────────────────────────
    const pmesii = data.pmesii || data.PMESII || result.PMESII;
    if (pmesii) {
        if (y > pageHeight - 40) { doc.addPage(); applyBranding(); y = 20; }
        y = addSection('IV. PMESII-PT FRAMEWORK ANALYSIS', y);
        const pmesiiRows = [
            ['POLITICAL', pmesii.political || '—'],
            ['MILITARY', pmesii.military || '—'],
            ['ECONOMIC', pmesii.economic || '—'],
            ['SOCIAL', pmesii.social || '—'],
            ['INFRASTRUCTURE', pmesii.infrastructure || '—'],
            ['INFORMATION', pmesii.information || '—'],
        ];
        autoTable(doc, {
            startY: y, body: pmesiiRows, theme: 'grid',
            styles: { fontSize: 8, cellPadding: 3, textColor: COLORS.TEXT, overflow: 'linebreak' },
            columnStyles: {
                0: { fontStyle: 'bold', cellWidth: 36, fillColor: [248, 250, 252] as [number,number,number] },
                1: { cellWidth: contentWidth - 36 }
            },
            margin: { left: margin, right: margin }, didDrawPage: () => applyBranding()
        });
        y = (doc as any).lastAutoTable.finalY + 10;
    }

    // ── RECENT EVENTS ──────────────────────────────────────────────────────
    const recentEvents = data.recentEvents || result.recentEvents;
    if (recentEvents?.length > 0) {
        if (y > pageHeight - 40) { doc.addPage(); applyBranding(); y = 20; }
        y = addSection('V. INTELLIGENCE FEED: RECENT DEVELOPMENTS', y);
        const evRows = recentEvents.slice(0, 10).map((e: any) => [formatLocalDate(e.date, e.date || 'RECENT'), e.title || '', e.description || '', e.impact || '—']);
        autoTable(doc, {
            startY: y, head: [['DATE', 'EVENT', 'DESCRIPTION', 'IMPACT']],
            body: evRows, theme: 'striped',
            headStyles: { fillColor: COLORS.PRIMARY, fontSize: 7.5 },
            styles: { fontSize: 7.5, cellPadding: 2.5, overflow: 'linebreak', textColor: COLORS.TEXT },
            columnStyles: { 0: { cellWidth: 20 }, 3: { cellWidth: 22 } },
            margin: { left: margin, right: margin }, didDrawPage: () => applyBranding()
        });
        y = (doc as any).lastAutoTable.finalY + 10;
    }

    // ── SECURITY INCIDENTS ─────────────────────────────────────────────────
    const incidents = data.securityIncidents || result.securityIncidents;
    if (incidents?.length > 0) {
        if (y > pageHeight - 40) { doc.addPage(); applyBranding(); y = 20; }
        y = addSection('VI. TACTICAL SECURITY INCIDENTS', y);
        const incRows = incidents.slice(0, 12).map((s: any) => [s.type || '—', s.location || '—', formatLocalDate(s.date, s.date || '—'), s.description || '—']);
        autoTable(doc, {
            startY: y, head: [['TYPE', 'LOCATION', 'DATE', 'DESCRIPTION']],
            body: incRows, theme: 'grid',
            headStyles: { fillColor: [185, 28, 28] as [number,number,number], fontSize: 7.5 },
            styles: { fontSize: 7.5, cellPadding: 2.5, overflow: 'linebreak', textColor: COLORS.TEXT },
            columnStyles: { 0: { cellWidth: 25 }, 2: { cellWidth: 20 } },
            margin: { left: margin, right: margin }, didDrawPage: () => applyBranding()
        });
        y = (doc as any).lastAutoTable.finalY + 10;
    }

    // ── THREAT GROUPS ──────────────────────────────────────────────────────
    const threatGroups = data.threatGroups || result.threatGroups;
    if (threatGroups?.length > 0) {
        if (y > pageHeight - 40) { doc.addPage(); applyBranding(); y = 20; }
        y = addSection('VII. THREAT ACTOR LANDSCAPE', y);
        const tgRows = threatGroups.slice(0, 8).map((g: any) => [g.name || '—', g.type || '—', g.threatLevel || g.level || '—', g.summary || g.description || '—']);
        autoTable(doc, {
            startY: y, head: [['ACTOR / GROUP', 'TYPE', 'THREAT LEVEL', 'ASSESSMENT']],
            body: tgRows, theme: 'striped',
            headStyles: { fillColor: COLORS.PRIMARY, fontSize: 7.5 },
            styles: { fontSize: 7.5, cellPadding: 2.5, overflow: 'linebreak', textColor: COLORS.TEXT },
            columnStyles: { 0: { cellWidth: 35 }, 2: { cellWidth: 22, halign: 'center' } },
            margin: { left: margin, right: margin }, didDrawPage: () => applyBranding()
        });
        y = (doc as any).lastAutoTable.finalY + 10;
    }

    // ── TRAVEL & VISA ADVISORY ─────────────────────────────────────────────
    const travel = data.travelAdvisory || result.travelAdvisory;
    const visa = data.visaRequirements || result.visaRequirements;
    const isUnitedStates = isUnitedStatesReport({ ...result, ...data });
    if (travel || visa || isUnitedStates) {
        if (y > pageHeight - 40) { doc.addPage(); applyBranding(); y = 20; }
        y = addSection('VIII. TRAVEL & IMMIGRATION INTELLIGENCE', y);
        const travRows: string[][] = [];
        if (travel?.us_state_dept_level) travRows.push(['US State Dept Advisory Level', travel.us_state_dept_level]);
        if (travel?.evacuation_status) travRows.push(['Evacuation Status', travel.evacuation_status]);
        if (travel?.summary) travRows.push(['Travel Summary', travel.summary]);
        if (isUnitedStates) travRows.push(['Visa Requirements', US_ENTRY_SUMMARY]);
        else if (visa?.summary) travRows.push(['Visa Requirements', visa.summary]);
        if (travRows.length > 0) {
            autoTable(doc, {
                startY: y, body: travRows, theme: 'grid',
                styles: { fontSize: 8.5, cellPadding: 3, textColor: COLORS.TEXT, overflow: 'linebreak' },
                columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55, fillColor: [248, 250, 252] as [number,number,number] } },
                margin: { left: margin, right: margin }, didDrawPage: () => applyBranding()
            });
            y = (doc as any).lastAutoTable.finalY + 10;
        }
    }

    // ── MEDICAL INTELLIGENCE ───────────────────────────────────────────────
    const medical = data.medicalInfo || data.medicalInformation || result.medicalInformation || result.healthData;
    if (medical) {
        if (y > pageHeight - 40) { doc.addPage(); applyBranding(); y = 20; }
        y = addSection('IX. MEDICAL & HEALTH INTELLIGENCE', y);
        const medRows: string[][] = [];
        if (medical.who_alerts || medical.whoAlert) medRows.push(['WHO Alert Status', medical.who_alerts || medical.whoAlert]);
        if (medical.cdc_advisory) medRows.push(['CDC Travel Notice', medical.cdc_advisory]);
        if (medical.medical_infrastructure) medRows.push(['Medical Infrastructure', medical.medical_infrastructure]);
        if (medical.vaccination_requirements || (medical.vaccinations && medical.vaccinations.join)) {
            medRows.push(['Vaccination Requirements', medical.vaccination_requirements || (medical.vaccinations || []).join(', ')]);
        }
        if (medical.summary) medRows.push(['Health Summary', medical.summary]);
        if (medRows.length > 0) {
            autoTable(doc, {
                startY: y, body: medRows, theme: 'grid',
                styles: { fontSize: 8.5, cellPadding: 3, textColor: COLORS.TEXT, overflow: 'linebreak' },
                columnStyles: { 0: { fontStyle: 'bold', cellWidth: 50, fillColor: [240, 253, 244] as [number,number,number] } },
                margin: { left: margin, right: margin }, didDrawPage: () => applyBranding()
            });
            y = (doc as any).lastAutoTable.finalY + 10;
        }
    }

    // ── LEADERSHIP & GOVERNANCE ────────────────────────────────────────────
    const leadership = data.leadershipOverview || result.leadershipOverview;
    if (leadership) {
        if (y > pageHeight - 40) { doc.addPage(); applyBranding(); y = 20; }
        y = addSection('X. LEADERSHIP & GOVERNANCE', y);
        const lRows: string[][] = [];
        if (leadership.headOfState || leadership.executive) lRows.push(['Head of State / Executive', leadership.headOfState || leadership.executive]);
        if (leadership.politicalStructure) lRows.push(['Political Structure', leadership.politicalStructure]);
        lRows.push(['Current Status', getLeadershipStatus(leadership)]);
        if (leadership.stabilityOutlook || leadership.stability) lRows.push(['Stability Outlook', leadership.stabilityOutlook || leadership.stability]);
        if (lRows.length > 0) {
            autoTable(doc, {
                startY: y, body: lRows, theme: 'grid',
                styles: { fontSize: 8.5, cellPadding: 3, textColor: COLORS.TEXT, overflow: 'linebreak' },
                columnStyles: { 0: { fontStyle: 'bold', cellWidth: 50, fillColor: [248, 250, 252] as [number,number,number] } },
                margin: { left: margin, right: margin }, didDrawPage: () => applyBranding()
            });
            y = (doc as any).lastAutoTable.finalY + 10;
        }
        // Key Figures
        if (leadership.keyFigures?.length > 0) {
            if (y > pageHeight - 40) { doc.addPage(); applyBranding(); y = 20; }
            doc.setFont('helvetica', 'bold').setFontSize(9).setTextColor(...COLORS.SECONDARY);
            doc.text('KEY POLITICAL FIGURES', margin, y); y += 5;
            const figRows = leadership.keyFigures.slice(0, 8).map((f: any) => [f.name || '—', f.role || '—', f.influence || '—', f.bio || '—']);
            autoTable(doc, {
                startY: y, head: [['NAME', 'ROLE', 'INFLUENCE', 'PROFILE']],
                body: figRows, theme: 'striped',
                headStyles: { fillColor: COLORS.SECONDARY, fontSize: 7.5 },
                styles: { fontSize: 7.5, cellPadding: 2.5, overflow: 'linebreak', textColor: COLORS.TEXT },
                columnStyles: { 0: { cellWidth: 38 }, 2: { cellWidth: 22 } },
                margin: { left: margin, right: margin }, didDrawPage: () => applyBranding()
            });
            y = (doc as any).lastAutoTable.finalY + 10;
        }
    }

    // ── SCENARIOS ──────────────────────────────────────────────────────────
    const scenarios = data.scenarios || result.scenarios;
    if (scenarios?.length > 0) {
        if (y > pageHeight - 40) { doc.addPage(); applyBranding(); y = 20; }
        y = addSection('XI. STRATEGIC SCENARIOS', y);
        const scRows = scenarios.slice(0, 6).map((s: any) => [s.title || '—', s.likelihood || '—', s.impact || '—', s.description || '—']);
        autoTable(doc, {
            startY: y, head: [['SCENARIO', 'LIKELIHOOD', 'IMPACT', 'DESCRIPTION']],
            body: scRows, theme: 'grid',
            headStyles: { fillColor: COLORS.ACCENT, fontSize: 7.5 },
            styles: { fontSize: 7.5, cellPadding: 2.5, overflow: 'linebreak', textColor: COLORS.TEXT },
            columnStyles: { 1: { cellWidth: 22, halign: 'center' }, 2: { cellWidth: 22, halign: 'center' } },
            margin: { left: margin, right: margin }, didDrawPage: () => applyBranding()
        });
        y = (doc as any).lastAutoTable.finalY + 10;
    }

    // ── CARVER ASSESSMENT ──────────────────────────────────────────────────
    const carver = data.carverAssessment || result.carverAssessment;
    if (carver) {
        if (y > pageHeight - 40) { doc.addPage(); applyBranding(); y = 20; }
        y = addSection('XII. CARVER TARGET ASSESSMENT', y);
        const cRows = [
            ['Criticality', carver.criticality || '—'],
            ['Accessibility', carver.accessibility || '—'],
            ['Recuperability', carver.recuperability || '—'],
            ['Vulnerability', carver.vulnerability || '—'],
            ['Effect', carver.effect || '—'],
            ['Recognizability', carver.recognizability || '—'],
        ];
        if (carver.overallAssessment) cRows.push(['Overall Assessment', carver.overallAssessment]);
        autoTable(doc, {
            startY: y, body: cRows, theme: 'grid',
            styles: { fontSize: 8.5, cellPadding: 3, textColor: COLORS.TEXT, overflow: 'linebreak' },
            columnStyles: { 0: { fontStyle: 'bold', cellWidth: 40, fillColor: [248, 250, 252] as [number,number,number] } },
            margin: { left: margin, right: margin }, didDrawPage: () => applyBranding()
        });
        y = (doc as any).lastAutoTable.finalY + 10;
    }

    // ── PREDICTIVE OUTLOOK ─────────────────────────────────────────────────
    const predictive = data.predictiveAnalysis || result.predictiveAnalysis;
    if (predictive) {
        if (y > pageHeight - 40) { doc.addPage(); applyBranding(); y = 20; }
        y = addSection('XIII. PREDICTIVE ANALYSIS & 6-12 MONTH OUTLOOK', y);
        const predictiveText = typeof predictive === 'string' ? predictive : (predictive as any)?.geopoliticalOutlook || (predictive as any)?.summary || '';
        y = addText(predictiveText, y, 9);
    }

    // ── DISCLAIMER ─────────────────────────────────────────────────────────
    if (y > pageHeight - 30) { doc.addPage(); applyBranding(); y = 20; }
    doc.setDrawColor(...COLORS.BORDER).line(margin, y, pageWidth - margin, y); y += 5;
    doc.setFont('helvetica', 'italic').setFontSize(7).setTextColor(...COLORS.LIGHT_TEXT);
    const disclaimer = 'This report is generated by IntelOptics AI and is intended for internal strategic planning purposes only. All intelligence is derived from open-source reporting and AI synthesis. Verify critical data with primary sources prior to operational decisions.';
    const discLines = doc.splitTextToSize(disclaimer, contentWidth);
    discLines.forEach((l: string) => { doc.text(l, margin, y); y += 4; });

    const fileName = `Geopolitical_Brief_${sanitizeFilename(country)}_${Date.now()}.pdf`;
    forceDownload(doc, fileName);
    return true;
};

export const generateCountryReport = async (countryData: any) => {
    const doc = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4'
    });
    
    const dtg = getDTG();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - (margin * 2);

    const reportData = countryData.fullReportData || {};
    const country = countryData.countryName || "Unknown Territory";
    const customTitle = `Strategic Intelligence Report: ${country}`;

    const applyBranding = () => {
        doc.setFillColor(...COLORS.PRIMARY);
        doc.rect(0, 0, pageWidth, 2.5, 'F');
        doc.setFont('helvetica', 'bold').setFontSize(8).setTextColor(...COLORS.LIGHT_TEXT);
        doc.text("INTELOPTICS INSIGHTS // STRATEGIC INTELLIGENCE", margin, 10);
        doc.setFontSize(8).setTextColor(...COLORS.BADGE_BG);
        doc.text("CONFIDENTIAL // INTERNAL", pageWidth - margin, 10, { align: 'right' });
        
        doc.setFont('helvetica', 'normal').setFontSize(7).setTextColor(...COLORS.LIGHT_TEXT);
        doc.text(`DTG: ${dtg} | HUB ID: ${Math.random().toString(36).substring(7).toUpperCase()}`, margin, pageHeight - 10);
        doc.text(`PAGE ${doc.getNumberOfPages()}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
    };

    applyBranding();

    let currentY = 25;
    doc.setFont('helvetica', 'bold').setFontSize(22).setTextColor(...COLORS.PRIMARY);
    doc.text(customTitle.toUpperCase(), margin, currentY);
    currentY += 15;

    // Helper for recursive object flattening (matching generateGenericPdf)
    const flattenValue = (val: any): string => {
        if (val === null || val === undefined) return "N/A";
        const date = formatLocalDateValue(val);
        if (date) return date;
        if (typeof val === 'object' && val.stringValue !== undefined) return val.stringValue;
        if (Array.isArray(val)) return val.join(', ');
        return String(val);
    };

    // --- EXECUTIVE SUMMARY ---
    if (reportData.summary) {
        doc.setFont('helvetica', 'bold').setFontSize(14).setTextColor(...COLORS.PRIMARY).text("EXECUTIVE SUMMARY", margin, currentY);
        currentY += 8;
        doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(...COLORS.TEXT);
        const splitSummary = doc.splitTextToSize(reportData.summary, contentWidth);
        
        splitSummary.forEach((line: string) => {
            if (currentY > pageHeight - 20) {
                doc.addPage();
                applyBranding();
                currentY = 25;
            }
            doc.text(line, margin, currentY);
            currentY += 5.5;
        });
        currentY += 10;
    }

    // --- DATA SECTIONS ---
    Object.entries(reportData).forEach(([key, value]) => {
        if (key === 'summary' || !value) return;

        if (currentY > pageHeight - 40) {
            doc.addPage();
            applyBranding();
            currentY = 25;
        }

        const sectionTitle = key.replace(/([A-Z])/g, ' $1').toUpperCase();
        doc.setFillColor(...COLORS.ACCENT).rect(margin, currentY, 4, 7, 'F');
        doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(...COLORS.PRIMARY).text(sectionTitle, margin + 7, currentY + 6);
        currentY += 12;

        if (typeof value === 'object' && !Array.isArray(value)) {
            const tableBody = Object.entries(value).map(([k, v]) => [
                k.replace(/([A-Z])/g, ' $1').toUpperCase(),
                flattenValue(v)
            ]);

            autoTable(doc, {
                startY: currentY,
                body: tableBody,
                theme: 'grid',
                styles: { fontSize: 8.5, cellPadding: 3, textColor: COLORS.TEXT },
                columnStyles: { 
                    0: { fontStyle: 'bold', cellWidth: contentWidth * 0.3, fillColor: [248, 250, 252] },
                    1: { cellWidth: contentWidth * 0.7 }
                },
                margin: { left: margin, right: margin },
                didDrawPage: () => applyBranding()
            });
            currentY = (doc as any).lastAutoTable.finalY + 12;
        } else {
            const valStr = flattenValue(value);
            const splitVal = doc.splitTextToSize(valStr, contentWidth);
            doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(...COLORS.SECONDARY);
            
            splitVal.forEach((line: string) => {
                if (currentY > pageHeight - 20) {
                    doc.addPage();
                    applyBranding();
                    currentY = 25;
                }
                doc.text(line, margin, currentY);
                currentY += 5.5;
            });
            currentY += 12;
        }
    });

    const fileName = `${sanitizeFilename(country)}_Intelligence_Report.pdf`;
    forceDownload(doc, fileName);
    return true;
};

export const generateIntelligenceBrief = (strategicData: any, tacticalAlerts: any[]) => {
    const doc = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4'
    });
    
    const dtg = getDTG();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - (margin * 2);

    // Branding helper
    const applyBranding = () => {
        doc.setFillColor(...COLORS.PRIMARY);
        doc.rect(0, 0, pageWidth, 2.5, 'F');
        doc.setFont('helvetica', 'bold').setFontSize(8).setTextColor(...COLORS.LIGHT_TEXT);
        doc.text("INTELOPTICS INSIGHTS // TACTICAL INTELLIGENCE", margin, 10);
        doc.setFontSize(8).setTextColor(...COLORS.BADGE_BG);
        doc.text("CONFIDENTIAL // INTERNAL", pageWidth - margin, 10, { align: 'right' });
        
        doc.setFont('helvetica', 'normal').setFontSize(7).setTextColor(...COLORS.LIGHT_TEXT);
        doc.text(`DTG: ${dtg} | BATTLESPACE OVERWATCH`, margin, pageHeight - 10);
        doc.text(`PAGE ${doc.getNumberOfPages()}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
    };

    applyBranding();

    let currentY = 25;
    const countryName = strategicData.countryName || strategicData.country || "Global Region";
    
    doc.setFont('helvetica', 'bold').setFontSize(22).setTextColor(...COLORS.PRIMARY);
    doc.text(`INTELLIGENCE BRIEF: ${countryName.toUpperCase()}`, margin, currentY);
    currentY += 12;

    const score = Number(strategicData.riskScore || 0);
    doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(...COLORS.SECONDARY);
    doc.text(`OPERATIONAL RISK RATING: ${score}/10.0`, margin, currentY);
    
    // Status Badge
    const riskLabel = score > 7.5 ? "CRITICAL" : score > 4.5 ? "ELEVATED" : "STABLE";
    const riskColor = (score > 7.5 ? [185, 28, 28] : score > 4.5 ? [234, 88, 12] : [22, 163, 74]) as [number, number, number];
    
    doc.setFillColor(...riskColor);
    doc.rect(pageWidth - margin - 40, currentY - 8, 40, 10, 'F');
    doc.setTextColor(255, 255, 255).setFontSize(9).setFont('helvetica', 'bold');
    doc.text(riskLabel, pageWidth - margin - 20, currentY - 1.5, { align: 'center' });

    currentY += 15;
    doc.setDrawColor(...COLORS.BORDER).line(margin, currentY, pageWidth - margin, currentY);
    currentY += 10;

    doc.setFont('helvetica', 'bold').setFontSize(14).setTextColor(...COLORS.PRIMARY).text("RECENT TACTICAL INCIDENTS", margin, currentY);
    currentY += 8;
    
    autoTable(doc, {
        startY: currentY,
        head: [['DTG', 'CATEGORY', 'INCIDENT ANALYSIS', 'PRIORITY']],
        body: tacticalAlerts.map(alert => [
            formatPdfDateTime(alert.processedAt),
            (alert.type || 'General').toUpperCase(),
            alert.summary || 'Details withheld.',
            (alert.priority || 'Medium').toUpperCase()
        ]),
        theme: 'striped',
        headStyles: { fillColor: COLORS.PRIMARY, fontSize: 8 },
        styles: { fontSize: 8, cellPadding: 3, textColor: COLORS.TEXT },
        margin: { left: margin, right: margin },
        didDrawPage: () => applyBranding()
    });

    const fileName = `${sanitizeFilename(countryName)}_Tactical_Briefing_${new Date().getTime()}.pdf`;
    forceDownload(doc, fileName);
};
/**
 * Universal PDF Generator with recursive object handling
 */
export const generateGenericPdf = async (reportData: any, customTitle: string = "Intelligence Report") => {
    const doc = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
        putOnlyUsedFonts: true
    });
    doc.setLineHeightFactor(1.45);
    
    const dtg = getDTG();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - (margin * 2);

    // --- HELPER: NEW PAGE WITH BRANDING ---
    const addBrandedPage = (isFirstPage = false) => {
        if (!isFirstPage) doc.addPage();
        
        // Stylized Header Bar
        doc.setFillColor(...COLORS.PRIMARY);
        doc.rect(0, 0, pageWidth, 2.5, 'F');
        
        // Brand Title (Top Left)
        doc.setFont('helvetica', 'bold').setFontSize(8).setTextColor(...COLORS.LIGHT_TEXT);
        doc.text("INTELOPTICS INSIGHTS // STRATEGIC RISK MANAGEMENT", margin, 10);
        
        // Classification (Top Right)
        doc.setFontSize(8).setTextColor(...COLORS.BADGE_BG);
        doc.text("CONFIDENTIAL // INTERNAL USE ONLY", pageWidth - margin, 10, { align: 'right' });
        
        // Footer (Page Number & Timestamp)
        const totalPages = doc.getNumberOfPages();
        doc.setFont('helvetica', 'normal').setFontSize(7).setTextColor(...COLORS.LIGHT_TEXT);
        doc.text(`DTG: ${dtg} | GENERATED BY INTELOPTICS CORE`, margin, pageHeight - 10);
        doc.text(`PAGE ${totalPages}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
    };

    addBrandedPage(true);

    // --- 1. TITLE SEQUENCE ---
    let currentY = 25;
    doc.setFont('helvetica', 'bold').setFontSize(24).setTextColor(...COLORS.PRIMARY);
    const titleLines = doc.splitTextToSize(customTitle.toUpperCase(), contentWidth);
    doc.text(titleLines, margin, currentY);
    currentY += (titleLines.length * 10) + 5;

    // Report Meta Data
    doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(...COLORS.SECONDARY);
    doc.text(`REPORT ID: ${Math.random().toString(36).substring(2, 10).toUpperCase()}`, margin, currentY);
    doc.text(`ORIGIN: INTELOPTICS INTELLIGENCE HUB`, margin, currentY + 5);
    currentY += 15;

    doc.setDrawColor(...COLORS.BORDER).line(margin, currentY, pageWidth - margin, currentY);
    currentY += 10;

    // Helper for recursive object flattening
    const flattenValue = (val: any): string => {
        if (val === null || val === undefined) return "N/A";
        
        if (typeof val === 'object' && !Array.isArray(val)) {
            if (val.stringValue !== undefined) return flattenValue(val.stringValue);
            if (val.integerValue !== undefined) return String(val.integerValue);
            if (val.doubleValue !== undefined) return String(val.doubleValue);
            if (val.booleanValue !== undefined) return String(val.booleanValue);
            if (val.arrayValue !== undefined) {
                return Array.isArray(val.arrayValue.values) 
                    ? val.arrayValue.values.map((v: any) => flattenValue(v)).join(', ') 
                    : "[]";
            }
            if (val.mapValue !== undefined) {
                return flattenValue(val.mapValue.fields || {});
            }
        }

        if (Array.isArray(val)) {
            return val.map(flattenValue).join(', ');
        }
        
        if (typeof val === 'object') {
            return Object.entries(val)
                .map(([k, v]) => `${k.toUpperCase()}: ${flattenValue(v)}`)
                .join(' | ');
        }
        
        return String(val);
    };

    // --- 2. EXECUTIVE SUMMARY ---
    const summary = reportData.summary || reportData.executiveSummary || reportData.riskSummary || reportData.tacticalSummary || reportData.introduction || "";
    if (summary) {
        doc.setFont('helvetica', 'bold').setFontSize(14).setTextColor(...COLORS.PRIMARY).text("EXECUTIVE SUMMARY", margin, currentY);
        currentY += 8;
        doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(...COLORS.TEXT);
        const splitSummary = doc.splitTextToSize(summary, contentWidth);
        
        // Multi-page summary check
        splitSummary.forEach((line: string) => {
            if (currentY > pageHeight - 25) {
                addBrandedPage();
                currentY = 25;
            }
            doc.text(line, margin, currentY);
            currentY += 6;
        });
        currentY += 10;
    }

    // --- 3. SECTION MAPPING ---
    Object.entries(reportData).forEach(([key, value]) => {
        const displayDate = formatLocalDateValue(value);
        if (displayDate) value = displayDate;
        if (['summary', 'executiveSummary', 'riskSummary', 'tacticalSummary', 'introduction', 'reportType', 'title', 'id', 'iso3', 'country', 'state'].includes(key)) return;
        if (!value || (Array.isArray(value) && value.length === 0)) return;

        // Start a context check for section header
        if (currentY > pageHeight - 40) {
            addBrandedPage();
            currentY = 25;
        }

        const sectionTitle = key.replace(/([A-Z])/g, ' $1').toUpperCase();
        
        // Section Accent Box
        doc.setFillColor(...COLORS.ACCENT);
        doc.rect(margin, currentY, 4, 7, 'F');
        
        doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(...COLORS.PRIMARY).text(sectionTitle, margin + 7, currentY + 6);
        currentY += 12;

        if (Array.isArray(value)) {
            const tableBody = value.map(item => {
                const displayDate = formatLocalDateValue(item);
                if (displayDate) return [displayDate];
                if (typeof item === 'object' && item !== null) {
                    return [Object.entries(item).map(([k, v]) => `${k.toUpperCase()}: ${flattenValue(v)}`).join('\n')];
                }
                return [flattenValue(item)];
            });

            autoTable(doc, {
                startY: currentY,
                body: tableBody,
                theme: 'striped',
                headStyles: { fillColor: COLORS.PRIMARY },
                styles: { fontSize: 8.5, cellPadding: 4, textColor: COLORS.TEXT, overflow: 'linebreak' },
                margin: { left: margin, right: margin },
                didDrawPage: (data) => {
                    drawGlobalBranding(doc, dtg, pageWidth, pageHeight, margin);
                }
            });
            currentY = (doc as any).lastAutoTable.finalY + 12;
        } else if (typeof value === 'object' && value !== null) {
            // Render Object as a Key-Value Table to prevent "clumping"
            const tableBody = Object.entries(value).map(([k, v]) => [
                k.replace(/([A-Z])/g, ' $1').toUpperCase(),
                flattenValue(v)
            ]);

            autoTable(doc, {
                startY: currentY,
                body: tableBody,
                theme: 'grid',
                styles: { fontSize: 8.5, cellPadding: 4, textColor: COLORS.TEXT, overflow: 'linebreak' },
                columnStyles: { 
                    0: { fontStyle: 'bold', cellWidth: contentWidth * 0.3, fillColor: [248, 250, 252] }, // Light slate BG for keys
                    1: { cellWidth: contentWidth * 0.7 }
                },
                margin: { left: margin, right: margin },
                didDrawPage: (data) => {
                    drawGlobalBranding(doc, dtg, pageWidth, pageHeight, margin);
                }
            });
            currentY = (doc as any).lastAutoTable.finalY + 12;
        } else {
            const valStr = flattenValue(value);
            const splitVal = doc.splitTextToSize(valStr, contentWidth);
            doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(...COLORS.SECONDARY);
            
            splitVal.forEach((line: string) => {
                if (currentY > pageHeight - 20) {
                    addBrandedPage();
                    currentY = 25;
                }
                doc.text(line, margin, currentY);
                currentY += 5.5; // Slightly more line height
            });
            currentY += 12;
        }
    });

    const fileName = `${sanitizeFilename(customTitle)}_Report_${new Date().getTime()}.pdf`;
    forceDownload(doc, fileName);
    return true;
};

/**
 * Specialized high-fidelity PDF generator for Event Security Plans
 */
export const generateEventSecurityPlanPdf = async (result: EventSecurityPlanResult) => {
    const doc = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4'
    });

    const dtg = getDTG();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - (margin * 2);

    const applyBranding = () => {
        doc.setFillColor(...COLORS.PRIMARY);
        doc.rect(0, 0, pageWidth, 5, 'F');
        doc.setFont('helvetica', 'bold').setFontSize(7).setTextColor(...COLORS.LIGHT_TEXT);
        doc.text(`INTELOPTICS SRM SUITE // EVENT SECURITY PLAN`, margin, pageHeight - 10);
        doc.text(`DTG: ${dtg}`, pageWidth - margin - 40, pageHeight - 10);
    };

    // --- 1. HEADER SECTION ---
    applyBranding();
    doc.setFont('helvetica', 'bold').setFontSize(24).setTextColor(...COLORS.TEXT).text(result.eventName.toUpperCase(), margin, 25);
    doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(...COLORS.SECONDARY).text(`${result.location.address} | ${result.dates}`, margin, 32);
    
    doc.setDrawColor(...COLORS.ACCENT);
    doc.setLineWidth(1);
    doc.line(margin, 36, 100, 36);

    let currentY = 45;

    // --- 2. MISSION INTENT & INPUT REVIEW ---
    if (result.userInputReview) {
        doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(...COLORS.PRIMARY).text("MISSION INTEGRITY SYNC // INPUT REVIEW", margin, currentY);
        currentY += 8;
        
        const reviewLines = doc.splitTextToSize(result.userInputReview, contentWidth);
        doc.setFont('helvetica', 'italic').setFontSize(9).setTextColor(...COLORS.SECONDARY);
        reviewLines.forEach((line: string) => {
            if (currentY > pageHeight - 20) { doc.addPage(); applyBranding(); currentY = 25; }
            doc.text(line, margin, currentY);
            currentY += 5;
        });
        currentY += 10;
    }

    // --- 3. EXECUTIVE SUMMARY ---
    if (result.executiveSummary) {
        doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(...COLORS.PRIMARY).text("I. EXECUTIVE OPERATIONAL DIRECTIVE", margin, currentY);
        currentY += 8;
        
        const summaryLines = doc.splitTextToSize(result.executiveSummary, contentWidth);
        doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(...COLORS.TEXT);
        summaryLines.forEach((line: string) => {
            if (currentY > pageHeight - 20) { doc.addPage(); applyBranding(); currentY = 25; }
            doc.text(line, margin, currentY);
            currentY += 5;
        });
        currentY += 10;
    }

    if (result.localizedThreatAnalysis) {
        if (currentY > pageHeight - 40) { doc.addPage(); applyBranding(); currentY = 25; }
        doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(...COLORS.PRIMARY).text("II. LOCALIZED THREAT INTELLIGENCE", margin, currentY);
        currentY += 8;

        const summaryLines = doc.splitTextToSize(result.localizedThreatAnalysis.summary, contentWidth);
        doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(...COLORS.SECONDARY);
        summaryLines.forEach((line: string) => {
            if (currentY > pageHeight - 20) { doc.addPage(); applyBranding(); currentY = 25; }
            doc.text(line, margin, currentY);
            currentY += 5;
        });
        currentY += 8;

        autoTable(doc, {
            startY: currentY,
            head: [['RISK VECTOR', 'IMPACT', 'TACTICAL ANALYSIS']],
            body: result.localizedThreatAnalysis.localRisks.map(r => [r.risk.toUpperCase(), (r as any).impact || 'N/A', r.description]),
            theme: 'striped',
            headStyles: { fillColor: COLORS.PRIMARY, fontSize: 8 },
            styles: { fontSize: 8, cellPadding: 3 },
            columnStyles: { 0: { fontStyle: 'bold', cellWidth: 40 }, 1: { fontStyle: 'bold', cellWidth: 20 }, 2: { cellWidth: 'auto' } },
            didDrawPage: () => applyBranding()
        });
        currentY = (doc as any).lastAutoTable.finalY + 15;
    }

    // --- 4. RISK MANAGEMENT MATRIX ---
    if (result.securityRiskManagementPlan) {
        if (currentY > pageHeight - 40) { doc.addPage(); applyBranding(); currentY = 25; }
        doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(...COLORS.PRIMARY).text("III. SECURITY RISK MANAGEMENT MATRIX", margin, currentY);
        currentY += 8;

        autoTable(doc, {
            startY: currentY,
            head: [['ASSET', 'THREAT', 'LIKELIHOOD', 'IMPACT', 'MITIGATION STRATEGY']],
            body: result.securityRiskManagementPlan.riskMatrix.map(m => [
                m.asset, m.threat, m.likelihood, m.impact, m.mitigation
            ]),
            theme: 'grid',
            headStyles: { fillColor: COLORS.ACCENT, fontSize: 7 },
            styles: { fontSize: 7, cellPadding: 2.5 },
            didDrawPage: () => applyBranding()
        });
        currentY = (doc as any).lastAutoTable.finalY + 15;
    }

    // --- 5. ACCESS CONTROL ---
    if (result.accessControlPlan) {
        if (currentY > pageHeight - 40) { doc.addPage(); applyBranding(); currentY = 25; }
        doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(...COLORS.PRIMARY).text("IV. ACCESS CONTROL & PERIMETER INTEGRITY", margin, currentY);
        currentY += 8;
        
        const acText = `Perimeter: ${result.accessControlPlan.perimeterSecurity}\nScreening: ${result.accessControlPlan.screeningProcedures}\nAccreditation: ${result.accessControlPlan.accreditationSystem}`;
        const splitAC = doc.splitTextToSize(acText, contentWidth);
        doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(...COLORS.TEXT);
        splitAC.forEach((line: string) => {
            if (currentY > pageHeight - 20) { doc.addPage(); applyBranding(); currentY = 25; }
            doc.text(line, margin, currentY);
            currentY += 5;
        });
        currentY += 10;
    }

    // --- 6. EP DETAIL ---
    if (result.epDetail) {
        if (currentY > pageHeight - 40) { doc.addPage(); applyBranding(); currentY = 25; }
        doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(...COLORS.PRIMARY).text("V. VIP PROTECTION (EP) DETAIL", margin, currentY);
        currentY += 8;
        
        const epText = `Principal Protection: ${result.epDetail.principalProtection}\nMovement Plan: ${result.epDetail.movementPlan}\nSafe Havens: ${result.epDetail.safeHavens}`;
        const splitEP = doc.splitTextToSize(epText, contentWidth);
        doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(...COLORS.TEXT);
        splitEP.forEach((line: string) => {
            if (currentY > pageHeight - 20) { doc.addPage(); applyBranding(); currentY = 25; }
            doc.text(line, margin, currentY);
            currentY += 5;
        });
        currentY += 10;
    }

    // --- 7. HEALTH AND SAFETY ---
    if (result.healthAndSafetyPlan) {
        if (currentY > pageHeight - 40) { doc.addPage(); applyBranding(); currentY = 25; }
        doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(...COLORS.PRIMARY).text("VI. HEALTH, SAFETY & MEDICAL PLAN", margin, currentY);
        currentY += 8;
        
        const hsText = `${result.healthAndSafetyPlan.summary}\nKey Procedures: ${result.healthAndSafetyPlan.keyProcedures.join(', ')}\nCompliance: ${result.healthAndSafetyPlan.complianceNotes}`;
        const splitHS = doc.splitTextToSize(hsText, contentWidth);
        doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(...COLORS.TEXT);
        splitHS.forEach((line: string) => {
            if (currentY > pageHeight - 20) { doc.addPage(); applyBranding(); currentY = 25; }
            doc.text(line, margin, currentY);
            currentY += 5;
        });
        currentY += 10;
    }

    // --- 6. STAFFING & ADMINISTRATION ---
    if (result.staffingAndAdmin) {
        if (currentY > pageHeight - 40) { doc.addPage(); applyBranding(); currentY = 25; }
        doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(...COLORS.PRIMARY).text("VII. STAFFING & SECURITY ADMINISTRATION", margin, currentY);
        currentY += 8;

        autoTable(doc, {
            startY: currentY,
            head: [['ROLE', 'QTY', 'PRIMARY RESPONSIBILITY']],
            body: result.staffingAndAdmin.staffingTable.map(s => [s.role, s.quantity, s.responsibility]),
            theme: 'striped',
            headStyles: { fillColor: COLORS.PRIMARY, fontSize: 8 },
            styles: { fontSize: 8, cellPadding: 3 },
            didDrawPage: () => applyBranding()
        });
        currentY = (doc as any).lastAutoTable.finalY + 12;

        const adminText = doc.splitTextToSize(result.staffingAndAdmin.adminRequirements, contentWidth);
        doc.setFont('helvetica', 'italic').setFontSize(8).setTextColor(...COLORS.SECONDARY);
        adminText.forEach((line: string) => {
            if (currentY > pageHeight - 20) { doc.addPage(); applyBranding(); currentY = 25; }
            doc.text(line, margin, currentY);
            currentY += 4.5;
        });
    }

    const fileName = `${sanitizeFilename(result.eventName)}_Security_Plan_${new Date().getTime()}.pdf`;
    forceDownload(doc, fileName);
    return true;
};

/**
 * Professional Certificate Generator
 */
export const generateCertificatePdf = (data: {
    studentName: string,
    courseName: string,
    organizationName?: string,
    completionDate: string,
    certificateId: string
}) => {
    const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
    });

    const width = doc.internal.pageSize.getWidth();
    const height = doc.internal.pageSize.getHeight();

    // 1. Decorative Border
    doc.setDrawColor(...COLORS.PRIMARY);
    doc.setLineWidth(1.5);
    doc.rect(10, 10, width - 20, height - 20);
    doc.setLineWidth(0.5);
    doc.rect(12, 12, width - 24, height - 24);

    // 2. IntelOptics logo, matching the app's concentric ring mark.
    doc.setFont('helvetica', 'bold').setFontSize(16).setTextColor(...COLORS.ACCENT);
    const logoX = (width - doc.getTextWidth('INTELOPTICS') - 16) / 2 + 6;
    doc.setFillColor(190, 207, 245).circle(logoX, 27, 6, 'F');
    doc.setFillColor(...COLORS.WHITE).circle(logoX, 27, 4.8, 'F');
    doc.setFillColor(...COLORS.ACCENT).circle(logoX, 27, 3.6, 'F');
    doc.setFillColor(...COLORS.WHITE).circle(logoX, 27, 2.4, 'F');
    doc.text('INTELOPTICS', logoX + 10, 29);
    doc.setFontSize(30).setTextColor(...COLORS.PRIMARY).text("CERTIFICATE OF COMPLETION", width / 2, 45, { align: 'center' });
    
    doc.setDrawColor(...COLORS.BORDER);
    doc.line(width / 2 - 50, 52, width / 2 + 50, 52);

    const drawFittedText = (text: string, x: number, y: number, maxWidth: number, fontSize: number) => {
        doc.setFontSize(fontSize);
        let lines = doc.splitTextToSize(text, maxWidth) as string[];
        while (lines.length > 2 && fontSize > 6) {
            doc.setFontSize(fontSize -= 0.5);
            lines = doc.splitTextToSize(text, maxWidth) as string[];
        }
        doc.text(lines, x, y, { align: 'center', lineHeightFactor: 1.15 });
    };

    // 3. Body
    doc.setFont('helvetica', 'normal').setFontSize(16).setTextColor(...COLORS.SECONDARY).text("This is to certify that", width / 2, 68, { align: 'center' });
    doc.setTextColor(...COLORS.TEXT);
    drawFittedText(data.studentName.toUpperCase(), width / 2, 85, width - 40, 32);
    doc.setFontSize(14).setTextColor(...COLORS.SECONDARY).text("has successfully completed all requirements for", width / 2, 112, { align: 'center' });
    doc.setTextColor(...COLORS.ACCENT);
    drawFittedText(data.courseName, width / 2, 126, width - 40, 22);

    // 4. Automatically populated organization and training team signatures.
    const signatories = getCertificateSignatories(data.organizationName);
    const signatureWidth = (width - 70) / 2;
    for (let i = 0; i < signatories.length; i++) {
        const x = 25 + signatureWidth / 2 + i * (signatureWidth + 20);
        doc.setFont('times', 'italic').setTextColor(...COLORS.PRIMARY);
        drawFittedText(signatories[i].name, x, 153, signatureWidth, 16);
        doc.setDrawColor(...COLORS.BORDER).line(x - signatureWidth / 2, 164, x + signatureWidth / 2, 164);
        doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(...COLORS.LIGHT_TEXT).text(signatories[i].role, x, 170, { align: 'center' });
        doc.setFontSize(8).text('Electronically signed', x, 175, { align: 'center' });
    }
    doc.setFontSize(10).setTextColor(...COLORS.SECONDARY).text(`Date of Completion: ${formatLocalDate(data.completionDate)}`, width / 2, 184, { align: 'center' });

    // 5. Footer & ID
    doc.setFontSize(8).setTextColor(...COLORS.LIGHT_TEXT).text(`Certificate ID: ${data.certificateId}`, 20, height - 17);
    doc.text("INTELOPTICS ACADEMY | STRATEGIC INTELLIGENCE TRAINING", width / 2, height - 17, { align: 'center' });

    forceDownload(doc, `${sanitizeFilename(`Certificate_${data.studentName}_${data.certificateId}`)}.pdf`);
    return true;
};

/**
 * Specialized high-fidelity PDF generator for Staff Protection Plans
 * Aligned with ISO 31030 and ASIS standards.
 */
export const generateStaffProtectionPlanPdf = async (result: EpThreatAnalysisResult) => {
    const doc = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4'
    });

    const dtg = getDTG();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - (margin * 2);

    const applyBranding = () => {
        doc.setFillColor(...COLORS.PRIMARY);
        doc.rect(0, 0, pageWidth, 5, 'F');
        doc.setFont('helvetica', 'bold').setFontSize(7).setTextColor(...COLORS.LIGHT_TEXT);
        doc.text(`INTELOPTICS SRM SUITE // STAFF PROTECTION PLAN`, margin, pageHeight - 10);
        doc.text(`CLASSIFICATION: PROTECTED // ASIS & ISO 31030 ALIGNED`, pageWidth - margin - 80, pageHeight - 10);
    };

    // --- 1. HEADER SECTION ---
    applyBranding();
    doc.setFont('helvetica', 'bold').setFontSize(22).setTextColor(...COLORS.PRIMARY).text("STAFF PROTECTION & EP OPERATIONS PLAN", margin, 25);
    doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(...COLORS.SECONDARY).text(`PRINCIPAL: ${result.principalName.toUpperCase()} | LOCATION: ${result.location} | DTG: ${dtg}`, margin, 32);
    
    doc.setDrawColor(...COLORS.ACCENT);
    doc.setLineWidth(1);
    doc.line(margin, 36, 100, 36);

    let currentY = 45;

    // --- 2. EXECUTIVE DIRECTIVE ---
    if (result.executiveSummary) {
        doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(...COLORS.PRIMARY).text("I. EXECUTIVE OPERATIONAL DIRECTIVE", margin, currentY);
        currentY += 8;
        
        const summaryLines = doc.splitTextToSize(result.executiveSummary, contentWidth);
        doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(...COLORS.TEXT);
        summaryLines.forEach((line: string) => {
            if (currentY > pageHeight - 20) { doc.addPage(); applyBranding(); currentY = 25; }
            doc.text(line, margin, currentY);
            currentY += 5;
        });
        currentY += 10;
    }

    // --- 3. THREAT LANDSCAPE ---
    if (result.directThreats && result.directThreats.length > 0) {
        if (currentY > pageHeight - 40) { doc.addPage(); applyBranding(); currentY = 25; }
        doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(...COLORS.PRIMARY).text("II. TACTICAL THREAT INTELLIGENCE", margin, currentY);
        currentY += 8;

        autoTable(doc, {
            startY: currentY,
            head: [['ACTOR/VECTOR', 'INTENT', 'CAPABILITY']],
            body: result.directThreats.map(t => [t.actor.toUpperCase(), t.intent, t.capability]),
            theme: 'striped',
            headStyles: { fillColor: COLORS.PRIMARY, fontSize: 8 },
            styles: { fontSize: 8, cellPadding: 3 },
            didDrawPage: () => applyBranding()
        });
        currentY = (doc as any).lastAutoTable.finalY + 15;
    }

    // --- 4. RISK MATRIX ---
    if (result.riskMatrix && result.riskMatrix.length > 0) {
        if (currentY > pageHeight - 40) { doc.addPage(); applyBranding(); currentY = 25; }
        doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(...COLORS.PRIMARY).text("III. OPERATIONAL RISK MATRIX", margin, currentY);
        currentY += 8;

        autoTable(doc, {
            startY: currentY,
            head: [['THREAT', 'LIKELIHOOD', 'IMPACT', 'SCORE']],
            body: result.riskMatrix.map(m => [m.threat, m.likelihood, m.impact, m.score]),
            theme: 'grid',
            headStyles: { fillColor: COLORS.ACCENT, fontSize: 8 },
            styles: { fontSize: 8, cellPadding: 3 },
            didDrawPage: () => applyBranding()
        });
        currentY = (doc as any).lastAutoTable.finalY + 15;
    }

    // --- 5. VULNERABILITIES & RECOMMENDATIONS ---
    if (result.vulnerabilities && result.vulnerabilities.length > 0) {
        if (currentY > pageHeight - 40) { doc.addPage(); applyBranding(); currentY = 25; }
        doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(...COLORS.PRIMARY).text("IV. VULNERABILITY GAP ANALYSIS", margin, currentY);
        currentY += 8;

        result.vulnerabilities.forEach(v => {
            if (currentY > pageHeight - 20) { doc.addPage(); applyBranding(); currentY = 25; }
            doc.setFont('helvetica', 'bold').setFontSize(9).setTextColor(...COLORS.SECONDARY).text(v.area.toUpperCase(), margin, currentY);
            currentY += 5;
            const descLines = doc.splitTextToSize(v.description, contentWidth - 5);
            doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(...COLORS.TEXT);
            descLines.forEach((line: string) => {
                doc.text(line, margin + 5, currentY);
                currentY += 5;
            });
            currentY += 3;
        });
        currentY += 5;
    }

    // --- 6. EXTRACTION & LOGISTICS ---
    if (result.extractionLogistics) {
        if (currentY > pageHeight - 40) { doc.addPage(); applyBranding(); currentY = 25; }
        doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(...COLORS.PRIMARY).text("V. EXTRACTION & EMERGENCY LOGISTICS", margin, currentY);
        currentY += 8;

        const logData = [
            ["NEAREST AIRPORT (POE/POD)", result.extractionLogistics.nearestAirport],
            ["LAND BORDER STATUS", result.extractionLogistics.landBorders],
            ["VISA / ENTRY REQS", result.extractionLogistics.visaRequirements],
            ["EMBASSY / DIPLOMATIC", result.extractionLogistics.embassyContact]
        ];

        autoTable(doc, {
            startY: currentY,
            body: logData,
            theme: 'grid',
            styles: { fontSize: 8.5, cellPadding: 3 },
            columnStyles: { 0: { fontStyle: 'bold', fillColor: [248, 250, 252], cellWidth: 50 }, 1: { cellWidth: 'auto' } },
            didDrawPage: () => applyBranding()
        });
        currentY = (doc as any).lastAutoTable.finalY + 15;
    }

    const fileName = `${sanitizeFilename(result.principalName)}_Protection_Plan_${new Date().getTime()}.pdf`;
    forceDownload(doc, fileName);
    return true;
};

/**
 * Specialized high-fidelity PDF generator for EP Operations Risk Analysis
 * Developed to match the mission-ready intelligence dashboard.
 */
export const generateEpOpsAnalysisPdf = async (result: EpOpsAnalysisResult) => {
    const doc = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4'
    });

    const dtg = getDTG();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - (margin * 2);

    const applyBranding = () => {
        doc.setFillColor(...COLORS.PRIMARY);
        doc.rect(0, 0, pageWidth, 5, 'F');
        doc.setFont('helvetica', 'bold').setFontSize(7).setTextColor(...COLORS.LIGHT_TEXT);
        doc.text(`INTELOPTICS SRM SUITE // EP OPERATIONS RISK ANALYSIS`, margin, pageHeight - 10);
        doc.text(`MISSION CLASSIFICATION: PROTECTED`, pageWidth - margin - 50, pageHeight - 10);
    };

    // --- 1. HEADER SECTION ---
    applyBranding();
    doc.setFont('helvetica', 'bold').setFontSize(22).setTextColor(...COLORS.PRIMARY).text("EP SECURITY & OPERATIONS ANALYSIS", margin, 25);
    doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(...COLORS.SECONDARY).text(`MISSION FOCUS: ${result.title || 'Security Analysis'} | DTG: ${dtg}`, margin, 32);
    
    doc.setDrawColor(...COLORS.ACCENT);
    doc.setLineWidth(1);
    doc.line(margin, 36, 100, 36);

    let currentY = 45;

    // --- 2. EXECUTIVE SUMMARY ---
    if (result.summary) {
        doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(...COLORS.PRIMARY).text("I. MISSION COMMAND DIRECTIVE", margin, currentY);
        currentY += 8;
        
        const summaryLines = doc.splitTextToSize(result.summary, contentWidth);
        doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(...COLORS.TEXT);
        summaryLines.forEach((line: string) => {
            if (currentY > pageHeight - 20) { doc.addPage(); applyBranding(); currentY = 25; }
            doc.text(line, margin, currentY);
            currentY += 5;
        });
        currentY += 10;
    }

    // --- 3. C2 & OPERATIONAL HQ ---
    if (result.commandAndControl || result.operationalHQ) {
        if (currentY > pageHeight - 40) { doc.addPage(); applyBranding(); currentY = 25; }
        doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(...COLORS.PRIMARY).text("II. COMMAND, CONTROL & OPERATIONAL HQ", margin, currentY);
        currentY += 8;

        const c2Data = [];
        if (result.commandAndControl) {
            c2Data.push(["COMMAND STRUCTURE", result.commandAndControl.structure]);
            c2Data.push(["COMMS PROTOCOLS", result.commandAndControl.communicationsProtocol]);
            c2Data.push(["ESCALATION PATH", result.commandAndControl.escalationPath]);
        }
        if (result.operationalHQ) {
            c2Data.push(["HQ SETUP", result.operationalHQ.setup]);
            c2Data.push(["HQ EQUIPMENT", result.operationalHQ.equipment]);
        }

        autoTable(doc, {
            startY: currentY,
            body: c2Data,
            theme: 'grid',
            styles: { fontSize: 8, cellPadding: 3 },
            columnStyles: { 0: { fontStyle: 'bold', fillColor: [248, 250, 252], cellWidth: 50 } },
            didDrawPage: () => applyBranding()
        });
        currentY = (doc as any).lastAutoTable.finalY + 15;
    }

    // --- 4. PERSONNEL & MANPOWER ---
    if (result.manpowerRequirements || (result.personnelQualifications && result.personnelQualifications.length > 0)) {
        if (currentY > pageHeight - 40) { doc.addPage(); applyBranding(); currentY = 25; }
        doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(...COLORS.PRIMARY).text("III. MANPOWER & PERSONNEL QUALIFICATIONS", margin, currentY);
        currentY += 8;

        if (result.manpowerRequirements) {
            doc.setFont('helvetica', 'bold').setFontSize(9).setTextColor(...COLORS.SECONDARY).text("OPERATIONAL MANPOWER REQUIREMENTS", margin, currentY);
            currentY += 5;
            doc.setFont('helvetica', 'normal').setFontSize(8.5).setTextColor(...COLORS.TEXT);
            doc.text(`Total Personnel Recommended: ${result.manpowerRequirements.totalPersonnel}`, margin + 5, currentY);
            currentY += 5;
            doc.text(`Shift Rotation Protocols: ${result.manpowerRequirements.shiftRotation}`, margin + 5, currentY);
            currentY += 10;
        }

        if (result.personnelQualifications && result.personnelQualifications.length > 0) {
            autoTable(doc, {
                startY: currentY,
                head: [['ROLE', 'SKILLS/KNOWLEDGE', 'EXPERIENCE/TRAINING']],
                body: result.personnelQualifications.map((q: any) => [
                    q.role, 
                    `${q.skills}\n${q.knowledge}`, 
                    `${q.experience}\n${q.training}`
                ]),
                theme: 'striped',
                headStyles: { fillColor: COLORS.PRIMARY, fontSize: 8 },
                styles: { fontSize: 7.5, cellPadding: 2.5 },
                didDrawPage: () => applyBranding()
            });
            currentY = (doc as any).lastAutoTable.finalY + 15;
        }
    }

    // --- 5. TACTICAL MARKERS ---
    if (result.mapMarkers && result.mapMarkers.length > 0) {
        if (currentY > pageHeight - 40) { doc.addPage(); applyBranding(); currentY = 25; }
        doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(...COLORS.PRIMARY).text("IV. GEOSPATIAL TACTICAL MAPPING (MARKERS)", margin, currentY);
        currentY += 8;

        autoTable(doc, {
            startY: currentY,
            head: [['TYPE', 'FACILITY/NAME', 'DESCRIPTION', 'COORDINATES']],
            body: result.mapMarkers.map((m: any) => [
                m.type.toUpperCase(),
                m.name,
                m.description,
                `${m.lat.toFixed(4)}, ${m.lng.toFixed(4)}`
            ]),
            theme: 'grid',
            headStyles: { fillColor: COLORS.ACCENT, fontSize: 8 },
            styles: { fontSize: 7.5, cellPadding: 2.5 },
            didDrawPage: () => applyBranding()
        });
        currentY = (doc as any).lastAutoTable.finalY + 15;
    }

    // --- 6. INTELLIGENCE ALERTS ---
    if (result.alerts && result.alerts.length > 0) {
        if (currentY > pageHeight - 40) { doc.addPage(); applyBranding(); currentY = 25; }
        doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(...COLORS.PRIMARY).text("V. REAL-TIME THREAT FEEDS & ALERTS", margin, currentY);
        currentY += 8;

        autoTable(doc, {
            startY: currentY,
            head: [['SEVERITY', 'EVENT TYPE', 'SUMMARY', 'TIMESTAMP']],
            body: result.alerts.map((a: any) => [
                a.severity.toUpperCase(),
                a.type,
                a.summary,
                a.timestamp
            ]),
            theme: 'striped',
            headStyles: { fillColor: [185, 28, 28], fontSize: 8 }, // Dark Red
            styles: { fontSize: 7.5, cellPadding: 2.5 },
            didDrawPage: () => applyBranding()
        });
        currentY = (doc as any).lastAutoTable.finalY + 15;
    }

    const fileName = `${sanitizeFilename(result.title || 'EP_Ops_Report')}_Intelligence_Report_${new Date().getTime()}.pdf`;
    forceDownload(doc, fileName);
    return true;
};

/**
 * Specialized high-fidelity PDF generator for HSSE Incident Reports.
 * Renders all OSHA-aligned incident fields in a structured, print-ready format.
 */
export const generateHSSEIncidentPdf = async (result: HSSEIncidentReport) => {
    const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });

    const dtg = getDTG();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - (margin * 2);

    const applyBranding = () => {
        doc.setFillColor(...COLORS.PRIMARY);
        doc.rect(0, 0, pageWidth, 5, 'F');
        doc.setFillColor(185, 28, 28); // Red HSSE accent stripe
        doc.rect(0, 5, pageWidth, 2, 'F');
        doc.setFont('helvetica', 'bold').setFontSize(7).setTextColor(...COLORS.LIGHT_TEXT);
        doc.text(`INTELOPTICS HSSE SUITE // INCIDENT REPORT`, margin, pageHeight - 10);
        doc.text(`DTG: ${dtg} | OSHA 300 ALIGNED`, pageWidth - margin, pageHeight - 10, { align: 'right' });
        doc.text(`PAGE ${doc.getNumberOfPages()}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
    };

    applyBranding();

    // --- 1. HEADER ---
    let currentY = 22;
    doc.setFont('helvetica', 'bold').setFontSize(20).setTextColor(...COLORS.PRIMARY);
    doc.text('HSSE INCIDENT REPORT', margin, currentY);
    currentY += 8;
    doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(...COLORS.SECONDARY);
    doc.text(`CASE NO: ${result.caseNumber || 'N/A'}  |  CLASSIFICATION: INTERNAL USE ONLY`, margin, currentY);
    currentY += 5;
    doc.setDrawColor(...COLORS.BORDER).line(margin, currentY, pageWidth - margin, currentY);
    currentY += 10;

    // --- 2. INCIDENT IDENTIFICATION TABLE ---
    doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(...COLORS.PRIMARY).text('I. INCIDENT IDENTIFICATION', margin, currentY);
    currentY += 7;
    autoTable(doc, {
        startY: currentY,
        body: [
            ['Case Number', result.caseNumber || 'N/A'],
            ['Date of Injury / Incident', formatLocalDate(result.dateOfInjury, 'N/A')],
            ['Time of Injury / Incident', result.timeOfInjury || 'N/A'],
            ['Location of Incident', result.locationOfIncident || 'N/A'],
        ],
        theme: 'grid',
        styles: { fontSize: 9, cellPadding: 3.5, textColor: COLORS.TEXT },
        columnStyles: {
            0: { fontStyle: 'bold', fillColor: [248, 250, 252], cellWidth: contentWidth * 0.35 },
            1: { cellWidth: contentWidth * 0.65 }
        },
        margin: { left: margin, right: margin },
        didDrawPage: () => applyBranding()
    });
    currentY = (doc as any).lastAutoTable.finalY + 12;

    // --- 3. AFFECTED PERSON ---
    if (currentY > pageHeight - 50) { doc.addPage(); applyBranding(); currentY = 22; }
    doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(...COLORS.PRIMARY).text('II. AFFECTED PERSON', margin, currentY);
    currentY += 7;
    autoTable(doc, {
        startY: currentY,
        body: [
            ['Full Name', result.employeeName || 'N/A'],
            ['Job Title / Position', result.jobTitle || 'N/A'],
        ],
        theme: 'grid',
        styles: { fontSize: 9, cellPadding: 3.5, textColor: COLORS.TEXT },
        columnStyles: {
            0: { fontStyle: 'bold', fillColor: [248, 250, 252], cellWidth: contentWidth * 0.35 },
            1: { cellWidth: contentWidth * 0.65 }
        },
        margin: { left: margin, right: margin },
        didDrawPage: () => applyBranding()
    });
    currentY = (doc as any).lastAutoTable.finalY + 12;

    // --- 4. INJURY / ILLNESS DESCRIPTION ---
    if (currentY > pageHeight - 50) { doc.addPage(); applyBranding(); currentY = 22; }
    doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(...COLORS.PRIMARY).text('III. INJURY / ILLNESS DESCRIPTION', margin, currentY);
    currentY += 7;
    autoTable(doc, {
        startY: currentY,
        body: [
            ['Nature of Injury / Illness', result.descriptionOfInjury || 'N/A'],
            ['Object / Substance Involved', result.objectOrSubstance || 'N/A'],
        ],
        theme: 'grid',
        styles: { fontSize: 9, cellPadding: 3.5, textColor: COLORS.TEXT, overflow: 'linebreak' },
        columnStyles: {
            0: { fontStyle: 'bold', fillColor: [248, 250, 252], cellWidth: contentWidth * 0.35 },
            1: { cellWidth: contentWidth * 0.65 }
        },
        margin: { left: margin, right: margin },
        didDrawPage: () => applyBranding()
    });
    currentY = (doc as any).lastAutoTable.finalY + 12;

    // --- 5. INCIDENT NARRATIVE ---
    if (currentY > pageHeight - 50) { doc.addPage(); applyBranding(); currentY = 22; }
    doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(...COLORS.PRIMARY).text('IV. INCIDENT NARRATIVE', margin, currentY);
    currentY += 8;
    if (result.incidentDescription) {
        doc.setFillColor(248, 250, 252);
        const narrativeLines = doc.splitTextToSize(result.incidentDescription, contentWidth - 10);
        const boxHeight = Math.min(Math.max(30, narrativeLines.length * 5.5 + 10), pageHeight - currentY - 25);
        doc.rect(margin, currentY - 3, contentWidth, boxHeight, 'F');
        doc.setDrawColor(...COLORS.BORDER);
        doc.rect(margin, currentY - 3, contentWidth, boxHeight);
        doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(...COLORS.TEXT);
        narrativeLines.forEach((line: string) => {
            if (currentY > pageHeight - 25) { doc.addPage(); applyBranding(); currentY = 22; }
            doc.text(line, margin + 5, currentY);
            currentY += 5.5;
        });
    }
    currentY += 15;

    // --- 6. CERTIFICATION FOOTER ---
    if (currentY > pageHeight - 40) { doc.addPage(); applyBranding(); currentY = 22; }
    doc.setDrawColor(...COLORS.BORDER).line(margin, currentY, pageWidth - margin, currentY);
    currentY += 8;
    doc.setFont('helvetica', 'bold').setFontSize(9).setTextColor(...COLORS.SECONDARY).text('SUPERVISOR CERTIFICATION', margin, currentY);
    currentY += 5;
    doc.setFont('helvetica', 'normal').setFontSize(8).setTextColor(...COLORS.LIGHT_TEXT);
    doc.text('I certify that this report is accurate and complete to the best of my knowledge.', margin, currentY);
    currentY += 12;
    doc.line(margin, currentY, margin + 70, currentY);
    doc.line(pageWidth - margin - 50, currentY, pageWidth - margin, currentY);
    currentY += 5;
    doc.setFontSize(7).setTextColor(...COLORS.LIGHT_TEXT);
    doc.text('Supervisor Signature', margin, currentY);
    doc.text('Date', pageWidth - margin - 50, currentY);

    const fileName = `HSSE_Incident_${sanitizeFilename(result.caseNumber || 'Report')}_${new Date().getTime()}.pdf`;
    forceDownload(doc, fileName);
    return true;
};

/**
 * Specialized high-fidelity PDF generator for Daily Micro Intelligence Reports.
 * Renders country risk score badge, PMESII snapshot, key indicators, and recent events
 * in a compact intelligence briefing format.
 */
export const generateDailyMicroReportPdf = async (reportData: any) => {
    reportData = redactDailyMicroReport(cleanReportPromptText(reportData));
    if (reportData.reportArea && Array.isArray(reportData.localEvents))
        return generateGenericPdf({
            summary: reportData.summary,
            reportArea: reportData.location,
            reportingWindow: `${formatPdfDateTime(reportData.windowStart)} to ${formatPdfDateTime(reportData.generatedAt)}`,
            localEvents: reportData.localEvents.length ? reportData.localEvents.map((event: any) => ({
                title: event.title, location: event.location, published: formatPdfDateTime(event.publishedAt),
                summary: event.summary,
            })) : 'No matching events found. This does not mean there is no risk.',
            coverageLimits: `${reportData.coverage.reviewedCount} dated, approved country events reviewed; ${reportData.coverage.unknownLocationCount} lacked a reliable state; ${reportData.coverage.unknownTimeCount} lacked a usable publication time. Only explicit state locations are included. Coverage may be incomplete.`,
            nationalContext: `${reportData.country}: country-wide background, not a state risk assessment. Updated: ${formatLocalDateTime(reportData.nationalContext.updatedAt, 'unknown')}.\n${reportData.nationalContext.summary}`,
        }, 'Daily Micro Report');
    const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });

    // Unwrap fullReportData wrapper if present (written by the high-fidelity pipeline).
    // Without this PMESII, summary, and risk score are all read from the wrong nesting level.
    const rd = enrichEconomicIntelligence(mergeFullReportData(reportData));

    const dtg = getDTG();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - (margin * 2);

    const applyBranding = () => {
        doc.setFillColor(...COLORS.PRIMARY);
        doc.rect(0, 0, pageWidth, 5, 'F');
        doc.setFont('helvetica', 'bold').setFontSize(7).setTextColor(...COLORS.LIGHT_TEXT);
        doc.text(`INTELOPTICS INSIGHTS // DAILY INTELLIGENCE MICRO-BRIEF`, margin, pageHeight - 10);
        doc.text(`DTG: ${dtg}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
        doc.text(`PAGE ${doc.getNumberOfPages()}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
    };

    applyBranding();

    const country = rd.country || rd.countryName || reportData.country || reportData.title || 'Global Intelligence';
    const riskScore = Number(rd.riskScore ?? rd.overallGeopoliticalRiskScore ?? reportData.riskScore ?? reportData.overallRiskScore ?? 0);
    // Standardised 0-100 scale thresholds — consistent with generateGeopoliticalAnalysisPdf
    const riskLevel: string = rd.overallRiskLevel || reportData.overallRiskLevel || reportData.riskLevel || (riskScore >= 80 ? 'CRITICAL' : riskScore >= 60 ? 'HIGH' : riskScore >= 35 ? 'MEDIUM' : 'LOW');
    const levelUpper = riskLevel.toUpperCase();
    const riskColor = (levelUpper === 'CRITICAL' ? [185, 28, 28] : levelUpper === 'HIGH' ? [194, 65, 12] : levelUpper === 'MEDIUM' ? [161, 98, 7] : [21, 128, 61]) as [number, number, number];

    // --- 1. HEADER BLOCK WITH RISK BADGE ---
    let currentY = 22;
    doc.setFont('helvetica', 'bold').setFontSize(16).setTextColor(...COLORS.PRIMARY);
    doc.text('DAILY INTELLIGENCE MICRO-BRIEF', margin, currentY);
    currentY += 8;
    doc.setFont('helvetica', 'bold').setFontSize(14).setTextColor(...COLORS.ACCENT);
    doc.text(country.toUpperCase(), margin, currentY);
    if (reportData.reportDate) {
        currentY += 5;
        doc.setFont('helvetica', 'normal').setFontSize(8).setTextColor(...COLORS.TEXT);
        doc.text(`${formatLocalDate(reportData.reportDate, reportData.reportDate)}${reportData.timeZone ? ` | ${reportData.timeZone}` : ''}`, margin, currentY);
    }

    // Risk score badge (top-right)
    const badgeX = pageWidth - margin - 38;
    doc.setFillColor(...riskColor);
    doc.roundedRect(badgeX, 12, 38, 18, 2, 2, 'F');
    doc.setFont('helvetica', 'bold').setFontSize(16).setTextColor(255, 255, 255);
    doc.text(String(riskScore), badgeX + 19, 22, { align: 'center' });
    doc.setFontSize(7);
    doc.text(levelUpper, badgeX + 19, 27, { align: 'center' });

    currentY += 6;
    doc.setDrawColor(...COLORS.BORDER).line(margin, currentY, pageWidth - margin, currentY);
    currentY += 10;

    // --- 2. SITUATION OVERVIEW ---
    const summary = rd.riskSummary || rd.summary || rd.executiveSummary || rd.advisorySummary || reportData.riskSummary || reportData.summary || reportData.executiveSummary || '';
    if (summary) {
        doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(...COLORS.PRIMARY).text('SITUATION OVERVIEW', margin, currentY);
        currentY += 6;
        const summaryLines = doc.splitTextToSize(summary, contentWidth);
        doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(...COLORS.TEXT);
        summaryLines.forEach((line: string) => {
            if (currentY > pageHeight - 25) { doc.addPage(); applyBranding(); currentY = 22; }
            doc.text(line, margin, currentY);
            currentY += 5.5;
        });
        currentY += 8;
    }

    // --- 3. PMESII SNAPSHOT ---
    const pmesii = rd.PMESII || rd.pmesii || reportData.PMESII || reportData.pmesii;
    if (pmesii && typeof pmesii === 'object') {
        if (currentY > pageHeight - 50) { doc.addPage(); applyBranding(); currentY = 22; }
        doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(...COLORS.PRIMARY).text('PMESII SNAPSHOT', margin, currentY);
        currentY += 6;
        const pmesiiRows = Object.entries(pmesii).map(([key, val]) => [
            key.toUpperCase(),
            typeof val === 'string' ? val : (val as any)?.description || (val as any)?.summary || String(val)
        ]);
        autoTable(doc, {
            startY: currentY,
            body: pmesiiRows,
            theme: 'striped',
            styles: { fontSize: 8, cellPadding: 3, textColor: COLORS.TEXT, overflow: 'linebreak' },
            columnStyles: {
                0: { fontStyle: 'bold', fillColor: [248, 250, 252], cellWidth: 30 },
                1: { cellWidth: contentWidth - 30 }
            },
            margin: { left: margin, right: margin },
            didDrawPage: () => applyBranding()
        });
        currentY = (doc as any).lastAutoTable.finalY + 12;
    }

    // Economic narrative and gaps accompany the structured figures below.
    const economicRows = [['Economic context', rd.economicIndicators.summary]];
    for (const [key, bankKey, label] of [['gdpGrowth', 'gdpGrowth', 'GDP growth'], ['inflation', 'inflation', 'Inflation'],
        ['unemployment', 'unemployment', 'Unemployment'], ['debtToGdp', 'debt_to_gdp', 'Debt to GDP'],
        ['gniPerCapita', 'gni_per_capita', 'GNI per capita'], ['foreignReserves', 'foreignReserves', 'Foreign reserves']])
        if (typeof rd.quantitativeIndicators.worldBank[bankKey] !== 'number')
            economicRows.push([label, rd.economicIndicators[key]]);
    if (currentY > pageHeight - 50) { doc.addPage(); applyBranding(); currentY = 22; }
    doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(...COLORS.PRIMARY).text('ECONOMIC INTELLIGENCE', margin, currentY);
    currentY += 6;
    autoTable(doc, {
        startY: currentY, body: economicRows, theme: 'striped',
        styles: { fontSize: 8, cellPadding: 3, textColor: COLORS.TEXT, overflow: 'linebreak' },
        margin: { left: margin, right: margin }, didDrawPage: () => applyBranding(),
    });
    currentY = (doc as any).lastAutoTable.finalY + 12;

    // --- 4. KEY RISK INDICATORS ---
    const indicatorGroups = quantitativeIndicatorGroups(rd.quantitativeIndicators || reportData.quantitativeIndicators);
    if (indicatorGroups.length > 0) {
        if (currentY > pageHeight - 50) { doc.addPage(); applyBranding(); currentY = 22; }
        doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(...COLORS.PRIMARY).text('KEY RISK INDICATORS', margin, currentY);
        currentY += 6;
        autoTable(doc, {
            startY: currentY,
            head: [['CATEGORY', 'INDICATOR', 'VALUE', 'PERIOD']],
            body: indicatorGroups.flatMap(group => group.rows.map(row => [
                group.title, row.label, row.value, row.period || '\u2014'
            ])),
            theme: 'grid',
            headStyles: { fillColor: COLORS.ACCENT, fontSize: 8 },
            styles: { fontSize: 8, cellPadding: 2.5, textColor: COLORS.TEXT },
            margin: { left: margin, right: margin },
            didDrawPage: () => applyBranding()
        });
        currentY = (doc as any).lastAutoTable.finalY + 12;
    }

    // --- 5. RECENT EVENTS OR SECTIONS ---
    const events = filterDailyMicroEvents(reportData, [
        ...(reportData.dailyEvents || rd.dailyEvents || []),
    ]);
    if (events && Array.isArray(events) && events.length > 0) {
        if (currentY > pageHeight - 50) { doc.addPage(); applyBranding(); currentY = 22; }
        doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(...COLORS.PRIMARY).text('REPORT DAY EVENTS', margin, currentY);
        currentY += 6;
        autoTable(doc, {
            startY: currentY,
            head: [['DATE', 'TYPE', 'EVENT SUMMARY']],
            body: events.map((e: any) => [
                formatPdfDateTime(e.publishedAt || e.pubDate || e.date || e.processedAt),
                (e.category || e.type || 'General').toUpperCase(),
                e.title || e.description || 'N/A'
            ]),
            theme: 'striped',
            headStyles: { fillColor: COLORS.PRIMARY, fontSize: 8 },
            styles: { fontSize: 8, cellPadding: 2.5, textColor: COLORS.TEXT, overflow: 'linebreak' },
            columnStyles: { 0: { cellWidth: 35 }, 1: { cellWidth: 28 }, 2: { cellWidth: 'auto' } },
            margin: { left: margin, right: margin },
            didDrawPage: () => applyBranding()
        });
        currentY = (doc as any).lastAutoTable.finalY + 12;
    }

    // --- 6. DISCLAIMER ---
    if (currentY > pageHeight - 30) { doc.addPage(); applyBranding(); currentY = 22; }
    doc.setDrawColor(...COLORS.BORDER).line(margin, currentY, pageWidth - margin, currentY);
    currentY += 5;
    const disclaimer = reportData.disclaimer || 'This micro-brief is derived from open-source intelligence and AI-assisted analysis. It is intended for informational planning purposes only and does not constitute official government guidance.';
    const disclaimerLines = doc.splitTextToSize(disclaimer, contentWidth);
    doc.setFont('helvetica', 'italic').setFontSize(7.5).setTextColor(...COLORS.LIGHT_TEXT);
    disclaimerLines.forEach((line: string) => { doc.text(line, margin, currentY); currentY += 4.5; });

    const fileName = `DailyMicro_${sanitizeFilename(country)}_${new Date().getTime()}.pdf`;
    forceDownload(doc, fileName);
    return true;
};

// ─── A3: HSSE Compliance Briefing ─────────────────────────────────────────────
export const generateHSSECompliancePdf = async (result: HSSEComplianceBriefing) => {
    const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
    const dtg = getDTG();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;

    const applyBranding = () => {
        doc.setFillColor(...COLORS.PRIMARY);
        doc.rect(0, 0, pageWidth, 2.5, 'F');
        doc.setFont('helvetica', 'bold').setFontSize(7).setTextColor(...COLORS.LIGHT_TEXT);
        doc.text('INTELOPTICS INSIGHTS // HSSE COMPLIANCE SUITE', margin, pageHeight - 10);
        doc.text(`DTG: ${dtg} | PAGE ${doc.getNumberOfPages()}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
    };

    applyBranding();
    let y = 20;
    doc.setFont('helvetica', 'bold').setFontSize(20).setTextColor(...COLORS.PRIMARY).text('HSSE COMPLIANCE BRIEFING', margin, y);
    y += 8;
    doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(...COLORS.SECONDARY).text(`${result.title || 'Compliance Report'} | DTG: ${dtg}`, margin, y);
    doc.setDrawColor(...COLORS.ACCENT).setLineWidth(0.8).line(margin, y + 3, pageWidth - margin, y + 3);
    y += 12;

    // Summary
    if (result.summary) {
        doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(...COLORS.PRIMARY).text('EXECUTIVE SUMMARY', margin, y);
        y += 6;
        const lines = doc.splitTextToSize(result.summary, contentWidth);
        doc.setFont('helvetica', 'normal').setFontSize(8.5).setTextColor(...COLORS.TEXT);
        lines.forEach((l: string) => { if (y > pageHeight - 20) { doc.addPage(); applyBranding(); y = 20; } doc.text(l, margin, y); y += 5; });
        y += 8;
    }

    // Sections
    const sections: any[] = (result as any).sections || (result as any).complianceSections || (result as any).additionalSections || [];
    sections.forEach((s: any) => {
        if (y > pageHeight - 40) { doc.addPage(); applyBranding(); y = 20; }
        const title = s.title || s.heading || '';
        const content = s.content || s.analysis || '';
        doc.setFillColor(...COLORS.ACCENT).rect(margin, y, 3, 6, 'F');
        doc.setFont('helvetica', 'bold').setFontSize(10).setTextColor(...COLORS.PRIMARY).text(title.toUpperCase(), margin + 6, y + 5);
        y += 10;
        const cLines = doc.splitTextToSize(content, contentWidth);
        doc.setFont('helvetica', 'normal').setFontSize(8.5).setTextColor(...COLORS.TEXT);
        cLines.slice(0, 8).forEach((l: string) => { if (y > pageHeight - 20) { doc.addPage(); applyBranding(); y = 20; } doc.text(l, margin, y); y += 5; });
        y += 6;
    });

    // Recommendations
    const recs: any[] = (result as any).recommendations || [];
    if (recs.length > 0) {
        if (y > pageHeight - 40) { doc.addPage(); applyBranding(); y = 20; }
        doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(...COLORS.PRIMARY).text('RECOMMENDATIONS', margin, y);
        y += 6;
        autoTable(doc, {
            startY: y,
            head: [['#', 'RECOMMENDATION']],
            body: recs.map((r: any, i: number) => [i + 1, typeof r === 'string' ? r : r.text || r.description || JSON.stringify(r)]),
            theme: 'striped',
            headStyles: { fillColor: COLORS.PRIMARY, fontSize: 8 },
            styles: { fontSize: 8, cellPadding: 2.5, overflow: 'linebreak' },
            columnStyles: { 0: { cellWidth: 10 } },
            margin: { left: margin, right: margin },
            didDrawPage: () => applyBranding()
        });
        y = (doc as any).lastAutoTable.finalY + 10;
    }

    forceDownload(doc, `HSSE_Compliance_${sanitizeFilename(result.title || 'Report')}_${Date.now()}.pdf`);
    return true;
};

// ─── A4: Audit Remediation Result ─────────────────────────────────────────────
export const generateAuditRemediationPdf = async (result: AuditRemediationResult) => {
    const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
    const dtg = getDTG();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;

    const PRIORITY_COLOR: Record<string, [number, number, number]> = {
        Critical: [185, 28, 28], High: [234, 88, 12], Medium: [202, 138, 4], Low: [22, 163, 74]
    };
    const priorityColor = PRIORITY_COLOR[result.priority] || COLORS.SECONDARY;

    const applyBranding = () => {
        doc.setFillColor(...COLORS.PRIMARY).rect(0, 0, pageWidth, 2.5, 'F');
        doc.setFont('helvetica', 'bold').setFontSize(7).setTextColor(...COLORS.LIGHT_TEXT);
        doc.text('INTELOPTICS INSIGHTS // SECURITY RISK AUDIT', margin, pageHeight - 10);
        doc.text(`DTG: ${dtg} | PAGE ${doc.getNumberOfPages()}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
    };

    applyBranding();
    let y = 20;

    // Header with priority badge
    doc.setFont('helvetica', 'bold').setFontSize(20).setTextColor(...COLORS.PRIMARY).text('SECURITY RISK AUDIT REPORT', margin, y);
    y += 8;
    doc.setFillColor(...priorityColor).rect(margin, y, 30, 7, 'F');
    doc.setFont('helvetica', 'bold').setFontSize(8).setTextColor(...COLORS.WHITE).text(`PRIORITY: ${result.priority?.toUpperCase()}`, margin + 2, y + 5);
    doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(...COLORS.SECONDARY).text(`Finding: ${result.findingTitle || result.title}`, margin + 35, y + 5);
    y += 14;

    // Meta row
    autoTable(doc, {
        startY: y,
        body: [
            ['FINDING ID', result.remediationId || 'N/A', 'ESTIMATED EFFORT', result.estimatedEffort || 'N/A'],
            ['LIKELIHOOD', result.likelihood || 'N/A', 'IMPACT', result.impact || 'N/A'],
            ['RESOURCE INTENSITY', result.resourceIntensity || 'N/A', 'SCORE', String(result.score ?? 'N/A')],
        ],
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 2.5 },
        columnStyles: { 0: { fontStyle: 'bold', fillColor: [248, 250, 252], cellWidth: 40 }, 2: { fontStyle: 'bold', fillColor: [248, 250, 252], cellWidth: 40 } },
        margin: { left: margin, right: margin },
        didDrawPage: () => applyBranding()
    });
    y = (doc as any).lastAutoTable.finalY + 10;

    // Executive summary
    if (result.executiveSummary) {
        doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(...COLORS.PRIMARY).text('EXECUTIVE SUMMARY', margin, y); y += 6;
        const lines = doc.splitTextToSize(result.executiveSummary, contentWidth);
        doc.setFont('helvetica', 'normal').setFontSize(8.5).setTextColor(...COLORS.TEXT);
        lines.forEach((l: string) => { if (y > pageHeight - 20) { doc.addPage(); applyBranding(); y = 20; } doc.text(l, margin, y); y += 5; });
        y += 8;
    }

    // Technical analysis
    if (result.technicalAnalysis) {
        if (y > pageHeight - 40) { doc.addPage(); applyBranding(); y = 20; }
        doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(...COLORS.PRIMARY).text('TECHNICAL ANALYSIS', margin, y); y += 6;
        const lines = doc.splitTextToSize(result.technicalAnalysis, contentWidth);
        doc.setFont('helvetica', 'normal').setFontSize(8.5).setTextColor(...COLORS.TEXT);
        lines.forEach((l: string) => { if (y > pageHeight - 20) { doc.addPage(); applyBranding(); y = 20; } doc.text(l, margin, y); y += 5; });
        y += 8;
    }

    // Risk factors
    if (result.riskFactors?.length > 0) {
        if (y > pageHeight - 40) { doc.addPage(); applyBranding(); y = 20; }
        doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(...COLORS.PRIMARY).text('RISK FACTOR MATRIX', margin, y); y += 6;
        autoTable(doc, {
            startY: y,
            head: [['RISK FACTOR', 'SCORE']],
            body: result.riskFactors.map(f => [f.factor, String(f.score)]),
            theme: 'striped',
            headStyles: { fillColor: COLORS.PRIMARY, fontSize: 8 },
            styles: { fontSize: 8, cellPadding: 2.5 },
            margin: { left: margin, right: margin },
            didDrawPage: () => applyBranding()
        });
        y = (doc as any).lastAutoTable.finalY + 10;
    }

    // Pathway actions
    if (result.pathwayActions?.length > 0) {
        if (y > pageHeight - 40) { doc.addPage(); applyBranding(); y = 20; }
        doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(...COLORS.PRIMARY).text('REMEDIATION PATHWAY', margin, y); y += 6;
        autoTable(doc, {
            startY: y,
            head: [['STEP', 'ACTION', 'COMPLEXITY', 'VALIDATION']],
            body: result.pathwayActions.map(a => [a.step, a.description, a.complexity, a.validationMethod]),
            theme: 'grid',
            headStyles: { fillColor: COLORS.ACCENT, fontSize: 8 },
            styles: { fontSize: 7.5, cellPadding: 2.5, overflow: 'linebreak' },
            margin: { left: margin, right: margin },
            didDrawPage: () => applyBranding()
        });
        y = (doc as any).lastAutoTable.finalY + 10;
    }

    // Disclaimer
    if (result.legalDisclaimer) {
        if (y > pageHeight - 30) { doc.addPage(); applyBranding(); y = 20; }
        doc.setDrawColor(...COLORS.BORDER).line(margin, y, pageWidth - margin, y); y += 5;
        const dLines = doc.splitTextToSize(result.legalDisclaimer, contentWidth);
        doc.setFont('helvetica', 'italic').setFontSize(7.5).setTextColor(...COLORS.LIGHT_TEXT);
        dLines.forEach((l: string) => { doc.text(l, margin, y); y += 4.5; });
    }

    forceDownload(doc, `AuditRemediation_${sanitizeFilename(result.findingTitle || 'Report')}_${Date.now()}.pdf`);
    return true;
};

// ─── A5: Geopolitical Government Analysis ────────────────────────────────────
export const generateGeopoliticalGovernmentPdf = async (result: GeopoliticalGovernmentAnalysisResult) => {
    const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
    const dtg = getDTG();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;

    const applyBranding = () => {
        doc.setFillColor(...COLORS.PRIMARY).rect(0, 0, pageWidth, 2.5, 'F');
        doc.setFont('helvetica', 'bold').setFontSize(7).setTextColor(...COLORS.LIGHT_TEXT);
        doc.text('INTELOPTICS INSIGHTS // GEOPOLITICAL GOVERNMENT ANALYSIS', margin, pageHeight - 10);
        doc.text(`DTG: ${dtg} | PAGE ${doc.getNumberOfPages()}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
    };

    applyBranding();
    let y = 20;

    doc.setFont('helvetica', 'bold').setFontSize(20).setTextColor(...COLORS.PRIMARY).text('GEOPOLITICAL GOVERNMENT ANALYSIS', margin, y); y += 8;
    doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(...COLORS.SECONDARY).text(`Country: ${result.country} | Risk Level: ${result.overallRiskLevel} | DTG: ${dtg}`, margin, y);
    doc.setDrawColor(...COLORS.ACCENT).line(margin, y + 3, pageWidth - margin, y + 3);
    y += 12;

    // Risk summary
    if (result.riskSummary) {
        doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(...COLORS.PRIMARY).text('STRATEGIC RISK SUMMARY', margin, y); y += 6;
        const lines = doc.splitTextToSize(result.riskSummary, contentWidth);
        doc.setFont('helvetica', 'normal').setFontSize(8.5).setTextColor(...COLORS.TEXT);
        lines.forEach((l: string) => { if (y > pageHeight - 20) { doc.addPage(); applyBranding(); y = 20; } doc.text(l, margin, y); y += 5; });
        y += 8;
    }

    // Authoritarianism index
    if (result.authoritarianismIndex) {
        if (y > pageHeight - 30) { doc.addPage(); applyBranding(); y = 20; }
        autoTable(doc, {
            startY: y,
            head: [['AUTHORITARIANISM INDEX']],
            body: [[`Classification: ${result.authoritarianismIndex.classification} | Score: ${result.authoritarianismIndex.score}`]],
            theme: 'grid',
            headStyles: { fillColor: COLORS.PRIMARY, fontSize: 8 },
            styles: { fontSize: 8.5, cellPadding: 3 },
            margin: { left: margin, right: margin },
            didDrawPage: () => applyBranding()
        });
        y = (doc as any).lastAutoTable.finalY + 10;
    }

    // Key leaders
    if (result.keyLeaders?.length) {
        if (y > pageHeight - 40) { doc.addPage(); applyBranding(); y = 20; }
        doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(...COLORS.PRIMARY).text('KEY LEADERSHIP PROFILES', margin, y); y += 6;
        autoTable(doc, {
            startY: y,
            head: [['NAME', 'ROLE', 'PROFILE']],
            body: result.keyLeaders.map(l => [l.name, l.role || 'N/A', l.bio || 'N/A']),
            theme: 'striped',
            headStyles: { fillColor: COLORS.PRIMARY, fontSize: 8 },
            styles: { fontSize: 8, cellPadding: 2.5, overflow: 'linebreak' },
            columnStyles: { 0: { cellWidth: 35 }, 1: { cellWidth: 30 } },
            margin: { left: margin, right: margin },
            didDrawPage: () => applyBranding()
        });
        y = (doc as any).lastAutoTable.finalY + 10;
    }

    // Risk matrix
    if (result.riskMatrix?.length) {
        if (y > pageHeight - 40) { doc.addPage(); applyBranding(); y = 20; }
        doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(...COLORS.PRIMARY).text('RISK FACTOR MATRIX', margin, y); y += 6;
        autoTable(doc, {
            startY: y,
            head: [['FACTOR', 'SCORE', 'SUMMARY']],
            body: result.riskMatrix.map(r => [r.factor, String(r.score), r.summary]),
            theme: 'grid',
            headStyles: { fillColor: COLORS.ACCENT, fontSize: 8 },
            styles: { fontSize: 8, cellPadding: 2.5, overflow: 'linebreak' },
            columnStyles: { 1: { cellWidth: 15, halign: 'center' } },
            margin: { left: margin, right: margin },
            didDrawPage: () => applyBranding()
        });
        y = (doc as any).lastAutoTable.finalY + 10;
    }

    // Detailed analysis sections
    if (result.detailedAnalysis?.length) {
        result.detailedAnalysis.forEach((s: any) => {
            if (y > pageHeight - 40) { doc.addPage(); applyBranding(); y = 20; }
            doc.setFillColor(...COLORS.ACCENT).rect(margin, y, 3, 6, 'F');
            doc.setFont('helvetica', 'bold').setFontSize(10).setTextColor(...COLORS.PRIMARY).text(s.title.toUpperCase(), margin + 6, y + 5);
            y += 10;
            const cLines = doc.splitTextToSize(s.content, contentWidth);
            doc.setFont('helvetica', 'normal').setFontSize(8.5).setTextColor(...COLORS.TEXT);
            cLines.slice(0, 10).forEach((l: string) => { if (y > pageHeight - 20) { doc.addPage(); applyBranding(); y = 20; } doc.text(l, margin, y); y += 5; });
            y += 8;
        });
    }

    // Predictive analysis
    if (result.predictiveAnalysis) {
        if (y > pageHeight - 40) { doc.addPage(); applyBranding(); y = 20; }
        doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(...COLORS.PRIMARY).text('PREDICTIVE ANALYSIS & OUTLOOK', margin, y); y += 6;
        const pLines = doc.splitTextToSize(result.predictiveAnalysis, contentWidth);
        doc.setFont('helvetica', 'normal').setFontSize(8.5).setTextColor(...COLORS.TEXT);
        pLines.forEach((l: string) => { if (y > pageHeight - 20) { doc.addPage(); applyBranding(); y = 20; } doc.text(l, margin, y); y += 5; });
    }

    forceDownload(doc, `GeopoliticalGov_${sanitizeFilename(result.country || 'Report')}_${Date.now()}.pdf`);
    return true;
};

// ─── A6: SRM Blueprint (SrmProject) ───────────────────────────────────────────
function appendSrmModuleReports(doc: jsPDF, modules: SrmProject['modules'], applyBranding: () => void) {
    const margin = 15;
    const contentWidth = doc.internal.pageSize.getWidth() - margin * 2;
    const pageHeight = doc.internal.pageSize.getHeight();
    let y = 20;
    for (const module of modules) {
        doc.addPage();
        applyBranding();
        y = 20;
        doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(...COLORS.PRIMARY);
        const title = doc.splitTextToSize(module.title || module.workflowId, contentWidth);
        doc.text(title, margin, y);
        y += title.length * 6 + 5;
        doc.setFont('helvetica', 'normal').setFontSize(8.5).setTextColor(...COLORS.TEXT);
        const reportModule = module.workflowId === 'daily_micro_report' ? { ...module,
            resultData: redactDailyMicroReport(srmModuleReportData(module.resultData ?? (module as any).data ?? (module as any).result)) } : module;
        const lines = doc.splitTextToSize(srmModuleReportText(reportModule), contentWidth);
        for (const line of lines) {
            if (y > pageHeight - 20) {
                doc.addPage();
                applyBranding();
                doc.setFont('helvetica', 'normal').setFontSize(8.5).setTextColor(...COLORS.TEXT);
                y = 20;
            }
            doc.text(line, margin, y);
            y += 4.5;
        }
    }
    return y + 8;
}

export const generateSrmBlueprintPdf = async (result: SrmProject) => {
    const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
    const dtg = getDTG();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;

    const STATUS_COLOR: Record<string, [number, number, number]> = {
        Active: [22, 163, 74], Ready: [37, 99, 235], 'In Progress': [202, 138, 4], Pending: [100, 116, 139]
    };

    const applyBranding = () => {
        doc.setFillColor(...COLORS.PRIMARY).rect(0, 0, pageWidth, 2.5, 'F');
        doc.setFont('helvetica', 'bold').setFontSize(7).setTextColor(...COLORS.LIGHT_TEXT);
        doc.text('INTELOPTICS INSIGHTS // SECURITY RISK MANAGEMENT SUITE', margin, pageHeight - 10);
        doc.text(`DTG: ${dtg} | PAGE ${doc.getNumberOfPages()}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
    };

    applyBranding();
    let y = 20;

    // Title block
    doc.setFont('helvetica', 'bold').setFontSize(20).setTextColor(...COLORS.PRIMARY).text('SRM STRATEGIC BLUEPRINT', margin, y); y += 8;
    doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(...COLORS.SECONDARY)
       .text(`Project: ${result.name || result.locationName} | Status: ${result.status} | DTG: ${dtg}`, margin, y);
    doc.setDrawColor(...COLORS.ACCENT).line(margin, y + 3, pageWidth - margin, y + 3);
    y += 13;

    // Project metadata
    autoTable(doc, {
        startY: y,
        body: [
            ['PROJECT NAME', result.name || result.locationName || 'N/A', 'STATUS', result.status || 'N/A'],
            ['LOCATION', result.locationName || 'N/A', 'FRAMEWORK', (result as any).activeFrameworkName || 'N/A'],
            ['CREATED', result.createdAt ? formatLocalDate(result.createdAt) : 'N/A', 'UPDATED', result.updatedAt ? formatLocalDate(result.updatedAt) : 'N/A'],
        ],
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 2.5 },
        columnStyles: {
            0: { fontStyle: 'bold', fillColor: [248, 250, 252], cellWidth: 38 },
            2: { fontStyle: 'bold', fillColor: [248, 250, 252], cellWidth: 38 }
        },
        margin: { left: margin, right: margin },
        didDrawPage: () => applyBranding()
    });
    y = (doc as any).lastAutoTable.finalY + 12;

    // Scope / description
    const description = (result as any).description || (result as any).scope || '';
    if (description) {
        doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(...COLORS.PRIMARY).text('PROJECT SCOPE', margin, y); y += 6;
        const lines = doc.splitTextToSize(description, contentWidth);
        doc.setFont('helvetica', 'normal').setFontSize(8.5).setTextColor(...COLORS.TEXT);
        lines.forEach((l: string) => { if (y > pageHeight - 20) { doc.addPage(); applyBranding(); y = 20; } doc.text(l, margin, y); y += 5; });
        y += 8;
    }

    // Module status table
    if (result.modules?.length > 0) {
        if (y > pageHeight - 40) { doc.addPage(); applyBranding(); y = 20; }
        doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(...COLORS.PRIMARY).text('STRATEGIC MODULES', margin, y); y += 6;
        autoTable(doc, {
            startY: y,
            head: [['MODULE', 'CATEGORY', 'STATUS', 'LAST UPDATED']],
            body: result.modules.map(m => [
                m.title,
                m.suiteId || 'N/A',
                m.status || 'Pending',
                (m as any).updatedAt ? formatLocalDate((m as any).updatedAt) : 'N/A'
            ]),
            theme: 'striped',
            headStyles: { fillColor: COLORS.PRIMARY, fontSize: 8 },
            styles: { fontSize: 8, cellPadding: 2.5, overflow: 'linebreak' },
            didDrawCell: (data: any) => {
                if (data.column.index === 2 && data.section === 'body') {
                    const status = data.cell.raw as string;
                    const color = STATUS_COLOR[status] || COLORS.SECONDARY;
                    doc.setTextColor(...color);
                    doc.setFont('helvetica', 'bold');
                }
            },
            margin: { left: margin, right: margin },
            didDrawPage: () => applyBranding()
        });
        y = (doc as any).lastAutoTable.finalY + 12;
    }

    // Audit log (last 5 entries)
    if (result.auditLog && result.auditLog.length > 0) {
        if (y > pageHeight - 40) { doc.addPage(); applyBranding(); y = 20; }
        doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(...COLORS.PRIMARY).text('AUDIT LOG (RECENT ACTIVITY)', margin, y); y += 6;
        const recentLogs = result.auditLog.slice().reverse().slice(0, 5);
        autoTable(doc, {
            startY: y,
            head: [['TIMESTAMP', 'USER', 'ACTION', 'DETAILS']],
            body: recentLogs.map(a => [
                a.timestamp ? formatLocalDateTime(a.timestamp) : 'N/A',
                a.user || 'System',
                a.action || 'N/A',
                a.details || 'N/A'
            ]),
            theme: 'grid',
            headStyles: { fillColor: COLORS.ACCENT, fontSize: 8 },
            styles: { fontSize: 7.5, cellPadding: 2.5, overflow: 'linebreak' },
            columnStyles: { 0: { cellWidth: 35 }, 1: { cellWidth: 28 } },
            margin: { left: margin, right: margin },
            didDrawPage: () => applyBranding()
        });
        y = (doc as any).lastAutoTable.finalY + 10;
    }

    appendSrmModuleReports(doc, [
        { id: 'summary', title: 'Executive Summary', description: '', workflowId: 'summary', suiteId: 'srm', status: 'Review',
            resultData: { content: cleanSrmSummary(result.summary) } }, ...(result.modules || [])
    ], applyBranding);
    forceDownload(doc, `SRM_Blueprint_${sanitizeFilename(result.name || result.locationName || 'Project')}_${Date.now()}.pdf`);
    return true;
};

/**
 * Generates a formal SRM Framework Conclusion Report PDF.
 * Called when a project is concluded.
 */
export const generateSrmConclusionReport = (project: SrmProject) => {
    const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
    const dtg = getDTG();
    const pageWidth  = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;

    const applyBranding = () => {
        doc.setFillColor(...COLORS.PRIMARY).rect(0, 0, pageWidth, 2.5, 'F');
        doc.setFont('helvetica', 'bold').setFontSize(7).setTextColor(...COLORS.LIGHT_TEXT);
        doc.text('INTELOPTICS INSIGHTS // SRM CONCLUSION REPORT', margin, 8);
        doc.setFontSize(7).setTextColor(...COLORS.BADGE_BG);
        doc.text('CONFIDENTIAL // INTERNAL USE ONLY', pageWidth - margin, 8, { align: 'right' });
        doc.setFont('helvetica', 'normal').setFontSize(6.5).setTextColor(...COLORS.LIGHT_TEXT);
        doc.text(`DTG: ${dtg}  |  INTELOPTICS CORE`, margin, pageHeight - 7);
        doc.text(`PAGE ${doc.getNumberOfPages()}`, pageWidth - margin, pageHeight - 7, { align: 'right' });
    };

    const addSection = (title: string, yPos: number): number => {
        if (yPos > pageHeight - 40) { doc.addPage(); applyBranding(); yPos = 20; }
        doc.setFillColor(...COLORS.ACCENT).rect(margin, yPos, 4, 7, 'F');
        doc.setFont('helvetica', 'bold').setFontSize(10).setTextColor(...COLORS.PRIMARY);
        doc.text(title, margin + 7, yPos + 5.5);
        return yPos + 14;
    };

    const addBodyText = (text: string, yPos: number, size = 9): number => {
        const lines = doc.splitTextToSize(String(text || '-'), contentWidth);
        doc.setFont('helvetica', 'normal').setFontSize(size).setTextColor(...COLORS.TEXT);
        lines.forEach((l: string) => {
            if (yPos > pageHeight - 18) { doc.addPage(); applyBranding(); yPos = 20; }
            doc.text(l, margin, yPos);
            yPos += size * 0.6;
        });
        return yPos + 4;
    };

    applyBranding();
    let y = 22;

    doc.setFillColor(21, 128, 61).roundedRect(pageWidth - margin - 36, 16, 36, 14, 2, 2, 'F');
    doc.setFont('helvetica', 'bold').setFontSize(7).setTextColor(255, 255, 255);
    doc.text('CONCLUDED', pageWidth - margin - 18, 22, { align: 'center' });
    doc.setFontSize(6).text('LIFECYCLE COMPLETE', pageWidth - margin - 18, 27, { align: 'center' });

    doc.setFont('helvetica', 'bold').setFontSize(18).setTextColor(...COLORS.PRIMARY);
    const titleLines = doc.splitTextToSize(`SRM CONCLUSION REPORT: ${(project.name || project.projectName || 'UNTITLED').toUpperCase()}`, contentWidth - 45);
    doc.text(titleLines, margin, y);
    y += titleLines.length * 8 + 4;

    const modules       = project.modules || [];
    const readyModules  = modules.filter((m: any) => m.status === 'Ready' || m.status === 'Active').length;
    const compliance    = modules.length ? Math.round((readyModules / modules.length) * 100) : 0;
    const acceptedRisks = (project.risks || []).filter((r: any) => r.status !== 'Pending' && r.status !== 'Discarded');
    const concludedDate = (project as any).concludedAt ? formatLocalDate((project as any).concludedAt) : 'N/A';

    doc.setFont('helvetica', 'normal').setFontSize(8).setTextColor(...COLORS.SECONDARY);
    doc.text(`Project: ${project.projectNumber || project.id.substring(0, 8).toUpperCase()}  |  Concluded: ${concludedDate}`, margin, y);
    y += 4;
    doc.setDrawColor(...COLORS.BORDER).line(margin, y, pageWidth - margin, y);
    y += 6;

    const kpis = [
        { label: 'Final Compliance', value: `${compliance}%` },
        { label: 'Modules', value: `${readyModules}/${modules.length}` },
        { label: 'Active Risks', value: String(acceptedRisks.length) },
        { label: 'Retention', value: '1 Year' },
    ];
    const kpiW = contentWidth / kpis.length;
    kpis.forEach((kpi, i) => {
        const x = margin + i * kpiW;
        doc.setFillColor(248, 250, 252).rect(x, y, kpiW - 3, 16, 'F');
        doc.setFont('helvetica', 'bold').setFontSize(13).setTextColor(...COLORS.ACCENT);
        doc.text(kpi.value, x + 4, y + 10);
        doc.setFont('helvetica', 'normal').setFontSize(7).setTextColor(...COLORS.LIGHT_TEXT);
        doc.text(kpi.label.toUpperCase(), x + 4, y + 14.5);
    });
    y += 22;

    if (project.summary) { y = addSection('I. EXECUTIVE SUMMARY', y); y = addBodyText(project.summary, y); }

    if (acceptedRisks.length > 0) {
        if (y > pageHeight - 40) { doc.addPage(); applyBranding(); y = 20; }
        y = addSection('II. ACTIVE RISK REGISTER', y);
        autoTable(doc, {
            startY: y,
            head: [['THREAT', 'CATEGORY', 'LIKELIHOOD', 'IMPACT', 'SCORE', 'MITIGATION']],
            body: acceptedRisks.map((r: any) => [r.threat || '-', r.category || '-', r.likelihood || '-', r.impact || '-', String(r.score ?? '-'), r.mitigation || '-']),
            theme: 'striped',
            headStyles: { fillColor: COLORS.PRIMARY, fontSize: 7 },
            styles: { fontSize: 7, cellPadding: 2, textColor: COLORS.TEXT, overflow: 'linebreak' },
            columnStyles: { 4: { cellWidth: 14, halign: 'center', fontStyle: 'bold' } },
            margin: { left: margin, right: margin }, didDrawPage: () => applyBranding(),
        });
        y = (doc as any).lastAutoTable.finalY + 10;
    }

    if (modules.length > 0) {
        if (y > pageHeight - 40) { doc.addPage(); applyBranding(); y = 20; }
        y = addSection('III. MODULE STATUS AT CONCLUSION', y);
        autoTable(doc, {
            startY: y,
            head: [['MODULE', 'STATUS', 'INTELLIGENCE CAPTURED']],
            body: modules.map((m: any) => [m.title || m.workflowId || '-', m.status || '-', m.resultData ? 'Yes' : 'No']),
            theme: 'grid',
            headStyles: { fillColor: COLORS.ACCENT, fontSize: 7.5 },
            styles: { fontSize: 8, cellPadding: 2.5, textColor: COLORS.TEXT },
            margin: { left: margin, right: margin }, didDrawPage: () => applyBranding(),
        });
        y = (doc as any).lastAutoTable.finalY + 10;
    }

    const auditLog = (project.auditLog || []).slice(0, 25);
    if (auditLog.length > 0) {
        if (y > pageHeight - 40) { doc.addPage(); applyBranding(); y = 20; }
        y = addSection('IV. LIFECYCLE AUDIT LOG', y);
        autoTable(doc, {
            startY: y,
            head: [['TIMESTAMP', 'USER', 'ACTION', 'DETAILS']],
            body: auditLog.map((e: any) => [e.timestamp ? formatLocalDateTime(e.timestamp) : '-', e.user || '-', e.action || '-', e.details || '-']),
            theme: 'striped',
            headStyles: { fillColor: COLORS.SECONDARY, fontSize: 7 },
            styles: { fontSize: 6.5, cellPadding: 2, textColor: COLORS.TEXT, overflow: 'linebreak' },
            columnStyles: { 0: { cellWidth: 30 }, 1: { cellWidth: 28 }, 2: { cellWidth: 30 } },
            margin: { left: margin, right: margin }, didDrawPage: () => applyBranding(),
        });
        y = (doc as any).lastAutoTable.finalY + 10;
    }

    if (modules.length)
        y = appendSrmModuleReports(doc, modules, applyBranding);
    if (y > pageHeight - 25) { doc.addPage(); applyBranding(); y = 20; }
    doc.setDrawColor(...COLORS.BORDER).line(margin, y, pageWidth - margin, y); y += 5;
    doc.setFont('helvetica', 'italic').setFontSize(6.5).setTextColor(...COLORS.LIGHT_TEXT);
    const notice = "RETENTION NOTICE: This record is subject to a 1-year retention policy from the date of archival. Verify compliance with applicable US federal and state requirements (NIST SP 800-53 / FISMA recommend 3+ years). Generated by IntelOptics Insights - internal use only.";
    doc.splitTextToSize(notice, contentWidth).forEach((l: string) => { doc.text(l, margin, y); y += 3.5; });

    forceDownload(doc, `SRM_Conclusion_${sanitizeFilename(project.name || project.projectName || 'Project')}_${Date.now()}.pdf`);
};
